import {
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonList,
  IonPopover,
  IonSpinner,
  useIonAlert,
  useIonRouter,
  useIonToast,
} from '@ionic/react';

import {
  addOutline,
  checkmarkOutline,
  closeOutline,
  createOutline,
  ellipsisVertical,
  eyeOffOutline,
  folderOpenOutline,
  musicalNotesOutline,
  searchOutline,
  star,
  starOutline,
  trashOutline,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { AddToCratePopover } from '../../components/common/AddToCratePopover';
import IntelligentMediaPlayer from '../../components/media-player/IntelligentMediaPlayer';
import ScanningVisualization from '../../components/visualization/ScanningVisualization';
import { useImportProgress } from '../../context/ImportProgressContext';
import { useLibraryStats } from '../../context/LibraryStatsContext';
import { on, once, removeListener, run } from '../../helper/electron';
import { useAddToCrate } from '../../hooks/useAddToCrate';
import { useIcons } from '../../hooks/useIcons';
import SpotifyService from '../../services/Spotify.service';
import { fetchCrateById } from '../../store/slices/crate.slice';
import { setScanning } from '../../store/slices/scanning.slice';
import { AppDispatch, RootState } from '../../store/store';
import { FilePathMap, iTrack } from '../../types';
import { performanceLogger } from '../../utils/performanceLogger';
import './MyLibrary.scss';

// Polyfill for requestIdleCallback
const requestIdleCallback =
  (window as any).requestIdleCallback ||
  ((callback: any) => setTimeout(callback, 1));

/*
 * IMPORTANT: Serato Play Count Field Usage
 *
 * There are TWO different play count fields in the Track interface:
 *
 * 1. seratoPlayCount (top-level field)
 *    - Populated at RUNTIME by calling Serato API (getAllTracks())
 *    - Contains CURRENT/LIVE play count data from Serato
 *    - Use this for displaying play counts in UI components
 *
 * 2. tags.serato_played_count (under tags object)
 *    - Stored in DATABASE from initial import
 *    - May be OUTDATED if user has played tracks since last import
 *    - Used for data conversion/storage purposes only
 *
 * Always use 'seratoPlayCount' for runtime display!
 */

interface TrackStatistics {
  totalFiles: number;
  totalArtists: number;
  totalAlbums: number;
  totalGenres: number;
  duplicates: number;
  // New metrics
  hasSerato?: boolean;
  missingGenreCount?: number;
  missingYearCount?: number;
  missingTitleOrArtistCount?: number;
  exactDuplicateCount?: number;
  lowBitrateCount?: number;
  missingBpmCount?: number;
  missingKeyCount?: number;
  missingFileCount?: number;
  unplayedCount?: number;
}

interface Track {
  id: number;
  filePath: string;
  isVideo: boolean;
  importSources: string[];
  tags?: {
    artist?: string[];
    title?: string;
    bpm?: number;
    key?: string;
    year?: number | string;
    genre?: string;
    album?: string;
    bitrate?: number;
    picture?: string;
    acoustid_id?: string;
    acoustid_fingerprint?: string;
    // Serato-specific metadata
    serato_color?: string;
    serato_played_count?: number;
    serato_rating?: number;
    serato_last_played?: string;
    serato_deck?: string;
    serato_beatgrid?: any[];
    serato_cues?: any[];
    serato_loops?: any[];
    duration?: number;
  };
  drive?: any;
  isDriveMounted?: boolean;
  seratoPlayCount?: number;
}

interface Album {
  id: string;
  name: string;
  releaseDate?: string;
  images?: Array<{
    height: number;
    width: number;
    url: string;
  }>;
  // ... existing code ...
}

// Add cell renderer components
const AlbumArtRenderer = (props: any) => {
  const { value } = props;
  return (
    <div className="album-art-cell">
      {value ? (
        <div className="track-album-art">
          <img src={`data:image/jpeg;base64,${value}`} alt="Album Art" />
        </div>
      ) : (
        <div className="track-album-art empty"></div>
      )}
    </div>
  );
};

const SeratoRatingRenderer = (props: any) => {
  const { value } = props;
  if (!value) return <span>-</span>;

  const totalStars = 5;
  const fullStars = Math.min(Math.floor(value), totalStars);

  return (
    <div className="serato-rating">
      {Array.from({ length: fullStars }).map((_, index) => (
        <IonIcon key={`star-${index}`} icon={star} className="star" />
      ))}
      {Array.from({ length: totalStars - fullStars }).map((_, index) => (
        <IonIcon
          key={`empty-${index}`}
          icon={starOutline}
          className="empty-star"
        />
      ))}
    </div>
  );
};

const SeratoPlaysRenderer = (props: any) => {
  const playCount = props.value || 0;
  return (
    <span className={`serato-plays ${playCount > 0 ? 'has-plays' : ''}`}>
      {playCount}
    </span>
  );
};

// Shared helpers for path normalization and friendly display
const normalizeFilePath = (
  filePath?: string,
  mountPoint?: string | null,
): string => {
  if (!filePath) return '';

  const normalizedPath = filePath.replace(/\\/g, '/');
  const hasDriveLetter = /^[a-zA-Z]:\//.test(normalizedPath);
  const hasNetworkPrefix = normalizedPath.startsWith('//');
  const startsAtRoot = normalizedPath.startsWith('/');

  if (hasDriveLetter || hasNetworkPrefix || startsAtRoot) {
    return normalizedPath;
  }

  const normalizedMountPoint =
    mountPoint?.replace(/\\/g, '/').replace(/\/+$/, '') || '';
  const trimmedRelative = normalizedPath.replace(/^\/+/, '');

  if (normalizedMountPoint) {
    return `${normalizedMountPoint}/${trimmedRelative}`;
  }

  return `/${trimmedRelative}`;
};

const applyNormalizedFilePath = (track: Track): Track => ({
  ...track,
  filePath: normalizeFilePath(track.filePath, track.drive?.mountPoint),
});

// Helper function to get filename from filePath
const getFileName = (filePath: string): string => {
  return filePath?.split('/').pop()?.split('\\').pop() || 'Unknown File';
};

// Source/DJ Software Renderer for first column
const SourceRenderer = (props: any) => {
  const { data: track } = props;
  const importSources = track?.importSources || [];

  // Determine which sources this track has
  const hasSerato = importSources.includes('serato');
  const hasFolder =
    importSources.includes('folder') || importSources.length === 0;

  if (!hasSerato && !hasFolder) return null;

  return (
    <div className="source-indicators">
      {hasSerato && (
        <div className="source-icon" title="Imported from Serato">
          <img
            src="/assets/icons/serato.png"
            alt="Serato"
            className="source-logo serato-source"
          />
        </div>
      )}
      {hasFolder && (
        <div className="source-icon" title="Local folder import">
          <IonIcon icon={folderOpenOutline} className="source-icon-folder" />
        </div>
      )}
    </div>
  );
};

// Key renderer with Mixed in Key color scheme
const KeyRenderer = (props: any) => {
  const key = props.value;
  if (!key) return <span className="key-badge no-key">-</span>;

  // Mixed in Key color scheme for Camelot keys
  const getKeyColor = (camelotKey: string): string => {
    const keyColors: { [key: string]: string } = {
      '1A': '#B8E6B8',
      '1B': '#B8E6B8', // Light green
      '2A': '#C8E6A0',
      '2B': '#C8E6A0', // Yellow-green
      '3A': '#E6E6A0',
      '3B': '#E6E6A0', // Light yellow
      '4A': '#E6D0A0',
      '4B': '#E6D0A0', // Light orange/peach
      '5A': '#E6B8A0',
      '5B': '#E6B8A0', // Light salmon
      '6A': '#E6A0B8',
      '6B': '#E6A0B8', // Light pink
      '7A': '#E6A0D0',
      '7B': '#E6A0D0', // Light rose
      '8A': '#D0A0E6',
      '8B': '#D0A0E6', // Light purple
      '9A': '#B8A0E6',
      '9B': '#B8A0E6', // Medium purple
      '10A': '#A0B8E6',
      '10B': '#A0B8E6', // Light blue-purple
      '11A': '#A0D0E6',
      '11B': '#A0D0E6', // Light blue
      '12A': '#A0E6E6',
      '12B': '#A0E6E6', // Light cyan
    };

    return keyColors[camelotKey] || '#CCCCCC';
  };

  // Convert standard key notation to Camelot if needed
  const convertToCamelot = (standardKey: string): string => {
    const keyMapping: { [key: string]: string } = {
      // Major keys (B)
      C: '8B',
      Cmaj: '8B',
      'C major': '8B',
      G: '9B',
      Gmaj: '9B',
      'G major': '9B',
      D: '10B',
      Dmaj: '10B',
      'D major': '10B',
      A: '11B',
      Amaj: '11B',
      'A major': '11B',
      E: '12B',
      Emaj: '12B',
      'E major': '12B',
      B: '1B',
      Bmaj: '1B',
      'B major': '1B',
      'F#': '2B',
      'F#maj': '2B',
      'F# major': '2B',
      Gb: '2B',
      Gbmaj: '2B',
      'C#': '3B',
      'C#maj': '3B',
      'C# major': '3B',
      Db: '3B',
      Dbmaj: '3B',
      Ab: '4B',
      Abmaj: '4B',
      'Ab major': '4B',
      'G#': '4B',
      'G#maj': '4B',
      Eb: '5B',
      Ebmaj: '5B',
      'Eb major': '5B',
      'D#': '5B',
      'D#maj': '5B',
      Bb: '6B',
      Bbmaj: '6B',
      'Bb major': '6B',
      'A#': '6B',
      'A#maj': '6B',
      F: '7B',
      Fmaj: '7B',
      'F major': '7B',

      // Minor keys (A)
      Am: '8A',
      'A minor': '8A',
      Amin: '8A',
      Em: '9A',
      'E minor': '9A',
      Emin: '9A',
      Bm: '10A',
      'B minor': '10A',
      Bmin: '10A',
      'F#m': '11A',
      'F# minor': '11A',
      'F#min': '11A',
      Gbm: '11A',
      'C#m': '12A',
      'C# minor': '12A',
      'C#min': '12A',
      Dbm: '12A',
      'G#m': '1A',
      'G# minor': '1A',
      'G#min': '1A',
      Abm: '1A',
      'D#m': '2A',
      'D# minor': '2A',
      'D#min': '2A',
      Ebm: '2A',
      'A#m': '3A',
      'A# minor': '3A',
      'A#min': '3A',
      Bbm: '3A',
      Fm: '4A',
      'F minor': '4A',
      Fmin: '4A',
      Cm: '5A',
      'C minor': '5A',
      Cmin: '5A',
      Gm: '6A',
      'G minor': '6A',
      Gmin: '6A',
      Dm: '7A',
      'D minor': '7A',
      Dmin: '7A',
    };

    return keyMapping[standardKey] || standardKey;
  };

  const camelotKey = convertToCamelot(key);
  const backgroundColor = getKeyColor(camelotKey);

  // Determine text color based on background brightness
  const getTextColor = (hexColor: string): string => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 160 ? '#333333' : '#FFFFFF';
  };

  const textColor = getTextColor(backgroundColor);

  // Create a slightly darker border color
  const borderColor = backgroundColor.replace(/,\s*([^,]+)\)/, ', 0.8)');

  return (
    <span
      className="key-badge"
      style={{
        backgroundColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
        backgroundImage: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}dd 100%)`,
      }}
    >
      {camelotKey}
    </span>
  );
};

// Kebab Menu Cell Renderer for track actions
const KebabMenuRenderer = (props: any) => {
  const {
    data: track,
    handleRowClick,
    setRowData,
    setFilteredTracks,
    onDeleted,
  } = props;
  const [showMenu, setShowMenu] = useState(false);
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();
  const kebabButtonRef = useRef<HTMLIonButtonElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [shouldOpenUpward, setShouldOpenUpward] = useState(false);

  // Convert track data to iTrack format for the hook
  const trackForCrate = {
    ID: track.id || track.spotifyId,
    Title: track.tags?.title || track.title || getFileName(track.filePath),
    Artist: track.tags?.artist || track.artist || 'Unknown',
    AlbumCover: track.tags?.picture?.[0]?.data || track.albumCover || '',
    BPM: track.tags?.bpm || track.bpm || 0,
    Key: track.tags?.key || track.key || '',
    PreviewURL: track.previewUrl || '',
  };

  const {
    crates,
    isLoading,
    searchText,
    setSearchText,
    showPopover,
    setShowPopover,
    handleAddTrackToCrate,
  } = useAddToCrate({
    track: trackForCrate,
    onSuccess: () => {
      present({
        message: `Track "${trackForCrate.Title}" added to crate`,
        duration: 2000,
        color: 'success',
      });
    },
    onError: (error) => {
      present({
        message: error,
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const handleEditMetadata = () => {
    setShowMenu(false);
    handleRowClick(track);
  };

  const deleteTrack = async (
    data: Track,
    options: { purge: boolean } = { purge: false },
    setRowData: React.Dispatch<React.SetStateAction<Track[]>>,
    setFilteredTracks: React.Dispatch<React.SetStateAction<Track[]>>,
    present: ReturnType<typeof useIonToast>[0],
  ) => {
    run('my-music/delete-track', { track: data, options });
    once('my-music/delete-track', (response: any) => {
      const isSuccess =
        response === 1 ||
        response === true ||
        (typeof response === 'number' && response > 0) ||
        (response &&
          typeof response === 'object' &&
          response.success !== false);

      if (isSuccess) {
        setRowData((prev) => prev.filter((item) => item.id !== data.id));
        setFilteredTracks((prev) => prev.filter((item) => item.id !== data.id));
        onDeleted?.();
        present({
          message: `Track "${data.tags?.title || 'Unknown'}" deleted`,
          duration: 2000,
          color: 'danger',
        });
      } else {
        const errorDetail =
          response && typeof response === 'object' && response.error
            ? `: ${response.error}`
            : '';
        present({
          message: `Failed to delete track "${data.tags?.title || 'Unknown'}"${errorDetail}`,
          duration: 2000,
          color: 'danger',
        });
      }
    });
  };

  const handleDeleteTrack = () => {
    setShowMenu(false);
    presentAlert({
      header: 'Confirm Delete',
      message: `Are you sure you want to delete "${track.tags?.title || getFileName(track.filePath)}"?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            deleteTrack(
              track,
              { purge: false },
              setRowData,
              setFilteredTracks,
              present,
            );
          },
        },
      ],
    });
  };

  const handleAddToCrate = () => {
    setShowMenu(false);
    setShowPopover(true);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (kebabButtonRef.current) {
      const rect = kebabButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuHeight = 150;
      setShouldOpenUpward(spaceBelow < menuHeight);
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className="kebab-menu-container">
      <IonButton
        ref={kebabButtonRef}
        id={`kebab-${track.id}`}
        fill="clear"
        className="kebab-menu-button"
        onClick={handleButtonClick}
      >
        <IonIcon icon={ellipsisVertical} />
      </IonButton>
      <IonPopover
        trigger={`kebab-${track.id}`}
        isOpen={showMenu}
        onDidDismiss={() => setShowMenu(false)}
        className={`kebab-menu-popover ${shouldOpenUpward ? 'force-upward' : ''}`}
        alignment="end"
        dismissOnSelect={true}
      >
        <IonList>
          <IonItem button onClick={handleEditMetadata}>
            <IonIcon icon={createOutline} slot="start" />
            Edit Metadata
          </IonItem>
          <IonItem button onClick={handleAddToCrate}>
            <IonIcon icon={addOutline} slot="start" />
            Add to Crate
          </IonItem>
          <IonItem button onClick={handleDeleteTrack}>
            <IonIcon icon={trashOutline} slot="start" />
            Delete Track
          </IonItem>
        </IonList>
      </IonPopover>
      <AddToCratePopover
        isOpen={showPopover}
        onDidDismiss={() => setShowPopover(false)}
        crates={crates}
        isLoading={isLoading}
        searchText={searchText}
        onSearchChange={setSearchText}
        onCrateSelect={handleAddTrackToCrate}
        fetchTracksOfCrate={async (crateId) => {
          const result = await dispatch(fetchCrateById(crateId));
          const crate = result?.payload as { Tracks?: any[] };
          return crate?.Tracks || [];
        }}
        onSubmitToTracks={async (crate, trackIds) => {
          const selectedTracks: FilePathMap = {};
          const selectedFilePath = normalizeFilePath(
            track.filePath,
            track.drive?.mountPoint,
          );

          for (const trackId of trackIds) {
            selectedTracks[trackId] = [selectedFilePath];
          }
          run('crates/save-track-file-paths-library', {
            crate_id: crate._id,
            filePaths: selectedTracks,
          });

          present({
            message: `Added to ${trackIds.length} tracks in ${crate.Title}`,
            duration: 2000,
            color: 'success',
          });
        }}
      />
    </div>
  );
};

// Define expected progress event data structure
interface RescanFoldersProgress {
  total: number; // Total files in the current folder being scanned
  numOfProcessed: number; // Files processed in the current folder
  folder: { path: string; id: string }; // Info about the folder being scanned
}

// Define return type for the helper
interface Suggestion {
  type: 'keep' | 'delete' | 'warn';
  text: string;
}

// --- Define the custom cell renderer ---
interface TitleCellRendererProps {
  value: string; // The title text
  data: Track; // The full track data object
}

// Custom Cell Renderer Component
const TitleWithSeratoIndicatorRenderer: React.FC<TitleCellRendererProps> = ({
  value,
  data,
}) => {
  const { icons } = useIcons(); // Assuming useIcons hook provides the Serato logo
  const isSeratoLinked = data?.importSources?.includes('serato');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {isSeratoLinked && icons.seratoLogo && (
        <img
          src={icons.seratoLogo}
          alt="Serato"
          style={{ width: '16px', height: '16px', verticalAlign: 'middle' }}
          title="Linked to Serato" // Tooltip
        />
      )}
      <span>{value}</span> {/* Display the track title */}
    </div>
  );
};
// --- End custom cell renderer ---

// Add tagFieldTypes and TrackUpdateData definitions
const tagFieldTypes: Record<string, 'string' | 'number' | 'array' | 'json'> = {
  artist: 'array',
  genre: 'string',
  bpm: 'number',
  year: 'number',
  album: 'string',
  title: 'string',
  picture: 'string',
};

type TrackUpdateData = {
  id: number;
  filePath?: string;
  isVideo?: boolean;
  importSources?: string[];
  tags?: any;
};

// Add comprehensive column definitions

// Enhanced TrackRow with dynamic column ordering
interface TrackRowProps {
  track: Track;
  index: number;
  // isSelected: boolean; // COMMENTED OUT: Selection functionality
  columnConfigs: ColumnConfig[];
  onRowClick: (track: Track) => void;
  onRowDoubleClick: (track: Track) => void;
  setRowData: React.Dispatch<React.SetStateAction<Track[]>>;
  setFilteredTracks: React.Dispatch<React.SetStateAction<Track[]>>;
  refreshStats: () => void;
}

const TrackRow: React.FC<TrackRowProps> = ({
  track,
  index,
  // isSelected, // COMMENTED OUT: Selection functionality
  columnConfigs,
  onRowClick,
  onRowDoubleClick,
  setRowData,
  setFilteredTracks,
  refreshStats,
}) => {
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const { icons } = useIcons();

  const handleCellDoubleClick = (
    columnKey: string,
    currentValue: string | number | string[],
  ) => {
    // Only allow editing for certain fields
    const editableFields = [
      'title',
      'artist',
      'album',
      'bpm',
      'year',
      'genre',
      'key',
    ];
    if (editableFields.includes(columnKey)) {
      setEditingCell(columnKey);
      setEditValue(String(currentValue));
    }
  };

  const handleCellEdit = (columnKey: string, newValue: string) => {
    // TODO: Implement actual save to backend
    console.log(`Editing ${columnKey} for track ${track.id}: "${newValue}"`);

    // For now, just update the local track object (this would be replaced with proper API call)
    if (track.tags) {
      switch (columnKey) {
        case 'title':
          track.tags.title = newValue;
          break;
        case 'artist':
          // Artist field expects string[] type, so always convert to array
          track.tags.artist = [newValue];
          break;
        case 'album':
          track.tags.album = newValue;
          break;
        case 'bpm':
          track.tags.bpm = parseInt(newValue) || 0;
          break;
        case 'year':
          track.tags.year = parseInt(newValue) || 0;
          break;
        case 'genre':
          track.tags.genre = newValue;
          break;
        case 'key':
          track.tags.key = newValue;
          break;
      }
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditKeyPress = (e: React.KeyboardEvent, columnKey: string) => {
    if (e.key === 'Enter') {
      handleCellEdit(columnKey, editValue);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Helper function to get filename from filePath
  const getFileName = (filePath: string): string => {
    return filePath?.split('/').pop()?.split('\\').pop() || 'Unknown File';
  };

  const getFieldValue = (field: string) => {
    switch (field) {
      case 'source':
        return track.importSources || [];
      case 'title':
        return track.tags?.title || getFileName(track.filePath);
      case 'artist':
        return Array.isArray(track.tags?.artist)
          ? track.tags?.artist?.join(', ') || 'Unknown'
          : track.tags?.artist || 'Unknown';
      case 'album':
        return track.tags?.album || 'Unknown';
      case 'bpm':
        return track.tags?.bpm || '-';
      case 'year':
        return track.tags?.year || '-';
      case 'genre':
        return track.tags?.genre || 'Unknown';
      case 'key':
        return track.tags?.key || '-';
      case 'seratoPlayCount':
        return track.seratoPlayCount || 0;
      case 'duration':
        const duration = track.tags?.duration;
        if (!duration) return '-';
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      case 'bitrate':
        return track.tags?.bitrate ? `${track.tags.bitrate}` : '-';
      case 'seratoRating':
        return track.tags?.serato_rating || 0;
      case 'filepath':
        return normalizeFilePath(track.filePath, track.drive?.mountPoint);
      default:
        return '-';
    }
  };

  const renderCellContent = (column: ColumnConfig) => {
    const value = getFieldValue(column.key);
    const isEditing = editingCell === column.key;
    const editableFields = [
      'title',
      'artist',
      'album',
      'bpm',
      'year',
      'genre',
      'key',
    ];
    const isEditable = editableFields.includes(column.key);

    switch (column.key) {
      case 'title':
        return (
          <div className="title-cell-content">
            <div className="title-actions">
              <KebabMenuRenderer
                data={track}
                handleRowClick={onRowDoubleClick}
                setRowData={setRowData}
                setFilteredTracks={setFilteredTracks}
                onDeleted={() => refreshStats?.()}
              />
            </div>
            <div className="title-content">
              <IntelligentMediaPlayer
                url={normalizeFilePath(track.filePath, track.drive?.mountPoint)}
                height={20}
                isDriveMounted={true}
              />
              {isEditing ? (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => handleCellEdit(column.key, editValue)}
                  onKeyDown={(e) => handleEditKeyPress(e, column.key)}
                  className="cell-edit-input"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className="track-title editable-cell"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    handleCellDoubleClick(column.key, value);
                  }}
                >
                  {value}
                </span>
              )}
              <div className="title-source-indicators">
                {track.importSources?.includes('serato') && (
                  <div className="source-icon" title="Imported from Serato">
                    {icons.seratoDJPro ? (
                      <img
                        src={icons.seratoDJPro}
                        alt="Serato DJ Pro"
                        className="source-logo serato-source"
                      />
                    ) : (
                      <img
                        src="/assets/icons/serato-dj-pro.svg"
                        alt="Serato DJ Pro"
                        className="source-logo serato-source"
                      />
                    )}
                  </div>
                )}
                {track.importSources?.includes('virtualdj') && (
                  <div className="source-icon" title="Imported from VirtualDJ">
                    {icons.virtualDJ ? (
                      <img
                        src={icons.virtualDJ}
                        alt="Virtual DJ"
                        className="source-logo"
                      />
                    ) : (
                      <img
                        src="/assets/icons/virtual-dj-logo.svg"
                        alt="Virtual DJ"
                        className="source-logo"
                      />
                    )}
                  </div>
                )}
                {track.importSources?.includes('folder') && (
                  <div className="source-icon" title="Local folder import">
                    <IonIcon
                      icon={folderOpenOutline}
                      className="source-icon-folder"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'key':
        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleCellEdit(column.key, editValue)}
              onKeyDown={(e) => handleEditKeyPress(e, column.key)}
              className="cell-edit-input"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleCellDoubleClick(column.key, value);
            }}
            className="editable-cell"
          >
            <KeyRenderer value={value} />
          </div>
        );
      case 'seratoRating':
        return <SeratoRatingRenderer value={value} />;
      case 'filepath':
        return (
          <span
            className="filepath-cell"
            title={String(value)}
            style={{
              fontSize: '12px',
              color: 'var(--ion-color-medium)',
              fontFamily: 'monospace',
            }}
          >
            {value}
          </span>
        );
      default:
        if (isEditing) {
          return (
            <input
              type={
                column.key === 'bpm' || column.key === 'year'
                  ? 'number'
                  : 'text'
              }
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleCellEdit(column.key, editValue)}
              onKeyDown={(e) => handleEditKeyPress(e, column.key)}
              className="cell-edit-input"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <span
            className={isEditable ? 'editable-cell' : ''}
            onDoubleClick={
              isEditable
                ? (e) => {
                    e.stopPropagation();
                    handleCellDoubleClick(column.key, value);
                  }
                : undefined
            }
          >
            {value}
          </span>
        );
    }
  };

  const visibleColumns = columnConfigs
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);

  return (
    <div
      className={`virtual-track-row ${index % 2 === 0 ? 'even' : 'odd'}`}
      onClick={() => onRowClick(track)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onRowDoubleClick(track);
      }}
    >
      {visibleColumns.map((column) => (
        <div
          key={column.key}
          className={`virtual-cell ${column.key}-cell`}
          style={{
            width: column.width,
            minWidth: column.minWidth,
            flex: column.flex,
          }}
        >
          {renderCellContent(column)}
        </div>
      ))}
    </div>
  );
};

// Column Configuration Interface
interface ColumnConfig {
  key: string;
  label: string;
  width?: number;
  minWidth?: number;
  flex?: number;
  sortable?: boolean;
  visible: boolean;
  order: number;
}

// All available columns with configuration
const ALL_AVAILABLE_COLUMNS: ColumnConfig[] = [
  {
    key: 'title',
    label: 'Title',
    flex: 3,
    minWidth: 250,
    sortable: true,
    visible: true,
    order: 0,
  },
  {
    key: 'artist',
    label: 'Artist',
    flex: 2,
    minWidth: 150,
    sortable: true,
    visible: true,
    order: 1,
  },
  {
    key: 'album',
    label: 'Album',
    flex: 2,
    minWidth: 150,
    sortable: true,
    visible: true,
    order: 2,
  },
  {
    key: 'bpm',
    label: 'BPM',
    width: 80,
    sortable: true,
    visible: true,
    order: 3,
  },
  {
    key: 'year',
    label: 'Year',
    width: 80,
    sortable: true,
    visible: true,
    order: 4,
  },
  {
    key: 'genre',
    label: 'Genre',
    flex: 2,
    minWidth: 150,
    sortable: true,
    visible: true,
    order: 5,
  },
  {
    key: 'key',
    label: 'Key',
    width: 70,
    sortable: true,
    visible: true,
    order: 6,
  },
  {
    key: 'seratoPlayCount',
    label: 'Plays',
    width: 80,
    sortable: true,
    visible: false,
    order: 7,
  },
  {
    key: 'duration',
    label: 'Duration',
    width: 90,
    sortable: true,
    visible: false,
    order: 8,
  },
  {
    key: 'bitrate',
    label: 'Bitrate',
    width: 90,
    sortable: true,
    visible: false,
    order: 9,
  },
  {
    key: 'seratoRating',
    label: 'Rating',
    width: 100,
    sortable: true,
    visible: false,
    order: 10,
  },
  {
    key: 'filepath',
    label: 'File Path',
    flex: 3,
    minWidth: 200,
    sortable: true,
    visible: false,
    order: 11,
  },
];

// Draggable Column Item Component
const DraggableColumnItem: React.FC<{
  column: ColumnConfig;
  index: number;
  onToggle: (key: string) => void;
  onReorder: (dragIndex: number, hoverIndex: number) => void;
}> = ({ column, index, onToggle, onReorder }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex !== index) {
      onReorder(dragIndex, index);
    }
  };

  return (
    <div
      className={`column-item ${isDragging ? 'dragging' : ''} ${dragOver ? 'drag-over' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'grab',
        backgroundColor: dragOver ? 'var(--ion-color-step-150)' : 'transparent',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
        borderRadius: '4px',
        margin: '2px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          color: 'var(--ion-color-medium)',
          cursor: 'grab',
        }}
      >
        <span style={{ fontSize: '12px' }}>⋮⋮</span>
      </div>
      <input
        type="checkbox"
        checked={column.visible}
        onChange={() => onToggle(column.key)}
        style={{ cursor: 'pointer' }}
        onClick={(e) => e.stopPropagation()}
      />
      <span style={{ flex: 1, fontSize: '14px' }}>{column.label}</span>
      <span style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
        {column.width
          ? `${column.width}px`
          : column.flex
            ? `flex ${column.flex}`
            : 'auto'}
      </span>
    </div>
  );
};

// Enhanced Column Management Modal
const ColumnManagementModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onColumnToggle: (key: string) => void;
  onColumnReorder: (dragIndex: number, hoverIndex: number) => void;
  onReset: () => void;
  onSelectAll: (checked: boolean) => void;
}> = ({
  isOpen,
  onClose,
  columns,
  onColumnToggle,
  onColumnReorder,
  onReset,
  onSelectAll,
}) => {
  if (!isOpen) return null;

  const visibleCount = columns.filter((col) => col.visible).length;
  const allSelected = visibleCount === columns.length;
  const someSelected = visibleCount > 0 && visibleCount < columns.length;

  return (
    <div className="column-management-modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h3>Manage Columns</h3>
          <button className="close-button" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <div className="modal-body">
          <div className="column-controls">
            <div className="controls-header">
              <span>Drag to reorder • Check to show/hide</span>
              <button className="reset-button" onClick={onReset}>
                Reset to Default
              </button>
            </div>
          </div>

          <div className="column-list">
            <div
              className="select-all-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                marginBottom: '8px',
                borderBottom:
                  '1px solid var(--ion-color-step-150, rgba(255, 255, 255, 0.15))',
                paddingBottom: '12px',
                cursor: 'pointer',
              }}
              onClick={() => onSelectAll(!allSelected)}
            >
              <div style={{ width: '12px' }} />
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = someSelected;
                  }
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelectAll(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
              />
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--ion-text-color, #fff)',
                  cursor: 'pointer',
                }}
              >
                Select All
              </span>
            </div>
            {columns.map((column, index) => (
              <DraggableColumnItem
                key={column.key}
                column={column}
                index={index}
                onToggle={onColumnToggle}
                onReorder={onColumnReorder}
              />
            ))}
          </div>

          <div className="modal-footer">
            <div className="visible-count">
              {visibleCount} of {columns.length} columns visible
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MyLibrary: React.FC<{
  scrollPosition: number;
}> = ({ scrollPosition }) => {
  // Performance logging
  // Reduce performance log noise in production; only log once per mount
  const initLoggedRef = useRef(false);

  const listenerRefs = useRef<{
    [key: string]: ((...args: any[]) => void) | undefined;
  }>({});

  useEffect(() => {
    return () => {
      Object.entries(listenerRefs.current).forEach(([eventName, listener]) => {
        if (listener) {
          removeListener(eventName, listener);
        }
      });
      listenerRefs.current = {};
    };
  }, []);

  useEffect(() => {
    if (!initLoggedRef.current) {
      performanceLogger.startTimer('MyLibrary - Component Initialization');
      initLoggedRef.current = true;
    }
    return () => {
      // ensure paired end on unmount once
      if (initLoggedRef.current) {
        performanceLogger.endTimer('MyLibrary - Component Initialization', {});
        initLoggedRef.current = false;
      }
    };
  }, []);

  const [present] = useIonToast();
  const dispatch = useDispatch();
  const { stats: statistics, healthScore, refreshStats } = useLibraryStats();
  const { getActiveJobs } = useImportProgress();
  // Health status is now derived from healthScore in the context
  const [rowData, setRowData] = useState<Track[]>([]);
  const [filteredTracks, setFilteredTracks] = useState<Track[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [selectedImportSource, setSelectedImportSource] = useState<
    string | null
  >(null);
  const [selectedDuplicateGroup, setSelectedDuplicateGroup] = useState<
    string | null
  >(null);
  const [showAutoPlaylist, setShowAutoPlaylist] = useState(false);
  const [isInitialScan, setIsInitialScan] = useState(false);
  const [folderProgress, setFolderProgress] = useState<Map<string, number>>(
    new Map(),
  );
  const [scanningFolders, setScanningFolders] = useState<string[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);
  // Ensure stats refresh immediately when landing on the page
  // Remove automatic refresh on mount; cached stats are good enough.

  // Derive overall import progress from global ImportProgressContext
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const jobs = getActiveJobs();
        if (!jobs.length) {
          setOverallProgress(0);
          return;
        }
        // Average percentage across active jobs
        const percents = jobs.map((j) => {
          const p = j.progress || ({} as any);
          if (!p || !p.totalTracks) return 0;
          const pct = Math.floor((p.processedTracks / p.totalTracks) * 100);
          return Math.min(pct, p.phase === 'complete' ? 100 : 99);
        });
        const avg = percents.reduce((a, b) => a + b, 0) / percents.length;
        setOverallProgress(Number.isFinite(avg) ? avg : 0);
      } catch {
        setOverallProgress(0);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [getActiveJobs]);
  const [topArtistsMap, setTopArtistsMap] = useState<Array<[string, number]>>(
    [],
  );
  const [topGenresMap, setTopGenresMap] = useState<Array<[string, number]>>([]);
  const [importSourcesMap, setImportSourcesMap] = useState<
    Array<[string, number]>
  >([]);
  const [filterData, setFilterData] = useState<{
    topArtists: Array<[string, number]>;
    topGenres: Array<[string, number]>;
    importSources: Array<[string, number]>;
  }>({ topArtists: [], topGenres: [], importSources: [] });
  const [duplicateGroupsMap, setDuplicateGroupsMap] = useState<
    Array<[string, Track[]]>
  >([]);
  const history = useHistory();
  const isScanning = useSelector(
    (state: RootState) => state.scanningState.isScanning,
  );
  const persistentPlayerVisible = useSelector(
    (state: RootState) => state.audioPlayerState.persistentPlayerVisible,
  );
  const [isResetting, setIsResetting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingEmoji, setLoadingEmoji] = useState('🎵');

  // Column management state
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('myLibrary_columnConfigs');
    if (saved) {
      try {
        const savedConfigs = JSON.parse(saved);
        // Merge saved configs with default configs to handle new columns
        return ALL_AVAILABLE_COLUMNS.map((defaultCol) => {
          const savedCol = savedConfigs.find(
            (c: ColumnConfig) => c.key === defaultCol.key,
          );
          return savedCol ? { ...defaultCol, ...savedCol } : defaultCol;
        }).sort((a, b) => a.order - b.order);
      } catch (error) {
        console.error('Error parsing saved column configs:', error);
        return ALL_AVAILABLE_COLUMNS;
      }
    }
    return ALL_AVAILABLE_COLUMNS;
  });

  // Derived state for visible columns (for backward compatibility)
  const visibleColumns = columnConfigs
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order)
    .map((col) => col.key);

  const tableMinWidth = columnConfigs
    .filter((col) => col.visible)
    .reduce((sum, col) => sum + (col.width || col.minWidth || 100), 0);

  // Column management modal state
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);

  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [metadataPanelVisible, setMetadataPanelVisible] = useState(false);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editedTags, setEditedTags] = useState<Record<string, any>>({});
  const nonEditableTags = ['key', 'bitrate'];
  const [metadataUpdating, setMetadataUpdating] = useState(false);
  const [resetStatus, setResetStatus] = useState<{
    step: number;
    totalSteps: number;
    message: string;
    details?: string;
    error?: string;
  } | null>(null);

  const [showAutoFillModal, setShowAutoFillModal] = useState(false);
  const [autoFillFields, setAutoFillFields] = useState<Record<string, boolean>>(
    {
      album: false,
      bpm: false,
      genre: false,
      year: false,
    },
  );

  const [isFoldersModalOpen, setIsFoldersModalOpen] = useState(false);

  const [scanCompletedWaitingForStats, setScanCompletedWaitingForStats] =
    useState(false);

  // Track active timeouts and listeners for cleanup
  const activeTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const activeListenersRef = useRef<Set<string>>(new Set());

  // Helper functions to track listeners and timeouts
  const trackListener = (listenerName: string) => {
    activeListenersRef.current.add(listenerName);
  };

  const untrackListener = (listenerName: string) => {
    activeListenersRef.current.delete(listenerName);
  };

  const trackTimeout = (timeoutId: NodeJS.Timeout) => {
    activeTimeoutsRef.current.add(timeoutId);
    return timeoutId;
  };

  const untrackTimeout = (timeoutId: NodeJS.Timeout) => {
    activeTimeoutsRef.current.delete(timeoutId);
    clearTimeout(timeoutId);
  };

  // Lazy loading state
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredTrackCount, setFilteredTrackCount] = useState(0); // Count of tracks matching current filter
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 1000; // Load 1000 tracks at a time

  // Refs for infinite scroll to avoid closure issues
  const hasMoreDataRef = useRef(hasMoreData);
  const isLoadingMoreRef = useRef(isLoadingMore);

  // Update refs when state changes
  useEffect(() => {
    hasMoreDataRef.current = hasMoreData;
  }, [hasMoreData]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Server-side filtering and sorting state
  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [currentSortField, setCurrentSortField] = useState('seratoPlayCount');
  const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>(
    'DESC',
  );

  // Function to load tracks with pagination (lazy loading)
  const loadData = useCallback(() => {
    performanceLogger.startTimer('MyLibrary - Load Tracks', {
      timestamp: Date.now(),
    });
    setIsLoading(true);
    setRowData([]);
    setFilteredTracks([]);
    setCurrentPage(0);
    setHasMoreData(true);

    loadPage(0);
  }, [currentFilters, currentSortField, currentSortOrder]); // Depend on filters and sorting

  // Function to load a specific page with explicit filters
  const loadPageWithFilters = useCallback(
    (
      page: number,
      filters?: any,
      sortField?: string,
      sortOrder?: 'ASC' | 'DESC',
    ) => {
      const finalFilters = filters !== undefined ? filters : currentFilters;
      const finalSortField = sortField || currentSortField;
      const finalSortOrder = sortOrder || currentSortOrder;

      performanceLogger.startTimer(`MyLibrary - Load Page ${page}`, {
        page,
        pageSize: PAGE_SIZE,
        filters: finalFilters,
        sortField: finalSortField,
        sortOrder: finalSortOrder,
      });

      console.log(
        `🔥 loadPageWithFilters: Using filters:`,
        finalFilters,
        `sort ${finalSortField} ${finalSortOrder}`,
      );

      try {
        const eventName = 'my-music/get-paginated';

        if (listenerRefs.current[eventName]) {
          removeListener(eventName, listenerRefs.current[eventName]!);
        }

        const handlePaginatedTracks = (response: any) => {
          const { tracks, totalCount, page: responsePage, hasMore } = response;

          console.log(
            `Loaded page ${responsePage}: ${tracks.length} tracks (${totalCount} total)`,
          );
          performanceLogger.endTimer(`MyLibrary - Load Page ${page}`, {
            trackCount: tracks.length,
            totalCount,
            page: responsePage,
            hasMore,
            success: true,
          });

          if (listenerRefs.current[eventName]) {
            removeListener(eventName, listenerRefs.current[eventName]!);
            delete listenerRefs.current[eventName];
          }

          // Ensure unique tracks by ID to prevent AG-Grid duplicate warnings
          const trackMap = new Map();
          const uniqueTracks: Track[] = [];

          tracks.forEach((track: any) => {
            if (track.id && !trackMap.has(track.id)) {
              trackMap.set(track.id, true);
              uniqueTracks.push(applyNormalizedFilePath(track));
            } else if (track.id) {
              console.warn(`Duplicate track ID found: ${track.id}, skipping`);
            } else {
              console.warn(`Track without ID found:`, track);
            }
          });

          console.log(
            `Filtered to ${uniqueTracks.length} unique tracks from ${tracks.length} total`,
          );

          // Update state
          setFilteredTrackCount(totalCount); // Count of tracks matching current filter
          setCurrentPage(responsePage);
          setHasMoreData(hasMore);
          setIsLoading(false);
          setIsLoadingMore(false);

          // If this is the first page, set the data directly
          if (responsePage === 0) {
            setRowData(uniqueTracks);
            setFilteredTracks(uniqueTracks);
            refreshStats?.();
          } else {
            // Append to existing data
            setRowData((prev) => [...prev, ...uniqueTracks]);
            setFilteredTracks((prev) => [...prev, ...uniqueTracks]);
          }

          // End the main load timer if this is the first page
          if (responsePage === 0) {
            performanceLogger.endTimer('MyLibrary - Load Tracks', {
              trackCount: uniqueTracks.length,
              totalCount,
              success: true,
            });
          }
        };

        listenerRefs.current[eventName] = handlePaginatedTracks;

        once(eventName, handlePaginatedTracks);
        run(eventName, {
          page,
          limit: PAGE_SIZE,
          filters: finalFilters,
          sortField: finalSortField,
          sortOrder: finalSortOrder,
        });
      } catch (error) {
        console.error('Error loading tracks:', error);
        performanceLogger.endTimer(`MyLibrary - Load Page ${page}`, {
          success: false,
          error: error instanceof Error ? error.message : String(error),
        });
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [PAGE_SIZE, currentFilters, currentSortField, currentSortOrder],
  );

  // Function to load a specific page with explicit sort parameters (uses current filters)
  const loadPageWithSort = useCallback(
    (page: number, sortField?: string, sortOrder?: 'ASC' | 'DESC') => {
      loadPageWithFilters(page, undefined, sortField, sortOrder);
    },
    [loadPageWithFilters],
  );

  // Function to load a specific page (uses current state)
  const loadPage = useCallback(
    (page: number) => {
      loadPageWithSort(page);
    },
    [loadPageWithSort],
  );

  // Function to load more data (for infinite scroll)
  const loadMoreData = useCallback(() => {
    if (!hasMoreData || isLoadingMore) return;

    setIsLoadingMore(true);
    loadPage(currentPage + 1);
  }, [hasMoreData, isLoadingMore, currentPage, loadPage]);

  // Function to fetch filter data (top artists and genres)
  const fetchFilterData = useCallback(() => {
    console.log('Fetching filter data...');

    // Get user's selected DJ software to pass to backend
    const selectedDJSoftware = JSON.parse(
      localStorage.getItem('selectedDJSoftware') || '[]',
    );

    once('track/get-filter-data', (data: any) => {
      console.log('Received filter data:', data);
      setFilterData(data);
      setTopArtistsMap(data.topArtists || []);
      setTopGenresMap(data.topGenres || []);
      setImportSourcesMap(data.importSources || []);
    });
    run('track/get-filter-data', { selectedDJSoftware });
  }, []);

  // Function to apply filters (triggers server-side filtering)
  const applyFilters = useCallback(
    (filters: any) => {
      console.log('Applying filters:', filters);
      setCurrentFilters(filters);
      // Reset to first page and reload data with new filters
      setCurrentPage(0);
      setHasMoreData(true);

      // Load data immediately with the new filters (don't wait for state update)
      performanceLogger.startTimer('MyLibrary - Load Tracks', {
        timestamp: Date.now(),
      });
      setIsLoading(true);
      setRowData([]);
      setFilteredTracks([]);

      // Load first page with explicit filters
      loadPageWithFilters(0, filters);
    },
    [loadPageWithFilters],
  );

  // Function to apply sorting (server-side sorting for entire dataset)
  const applySorting = useCallback(
    (sortField: string, sortOrder: 'ASC' | 'DESC') => {
      console.log('🔥 applySorting CALLED with:', sortField, sortOrder);

      setCurrentSortField(sortField);
      setCurrentSortOrder(sortOrder);

      // Reset to first page and reload data with new sort
      setCurrentPage(0);
      setHasMoreData(true);
      setRowData([]);
      setFilteredTracks([]);
      setIsLoading(true);

      console.log('🔥 About to call loadPageWithSort with new values');
      // Load first page with new sorting - pass values directly to avoid state timing issues
      loadPageWithSort(0, sortField, sortOrder);
    },
    [loadPageWithSort],
  );

  // Cleanup function for timeouts and listeners
  useEffect(() => {
    return () => {
      // Clear all active timeouts
      activeTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      activeTimeoutsRef.current.clear();

      // Remove all active listeners
      activeListenersRef.current.forEach((listenerName) => {
        removeListener(listenerName);
      });
      activeListenersRef.current.clear();
    };
  }, []);

  // Add folder management state
  const [isAddingFolder, setIsAddingFolder] = useState(false);

  // Add Serato import state
  const [isImportingSerato, setIsImportingSerato] = useState(false);

  // Remove import dropdown state - now navigates to dedicated page
  const [scanningProgress, setScanningProgress] = useState<{
    isScanning: boolean;
    currentFolder?: string;
    progress?: number;
    filesProcessed?: number;
    totalFiles?: number;
  }>({ isScanning: false });
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState<{
    isDeleting: boolean;
    message?: string;
  }>({ isDeleting: false });

  const { icons } = useIcons();

  const location = useLocation<{
    applyFilter?: boolean;
    activeFilter?: string;
  }>();

  const ionRouter = useIonRouter();

  const funMessages = [
    { message: 'Found some House music! Time to get groovy 🏠', emoji: '🕺' },
    {
      message: 'Ooh, Drum & Bass detected! My circuits are tingling!',
      emoji: '🥁',
    },
    { message: 'Is this Techno? *robot dancing intensifies*', emoji: '🤖' },
    { message: 'Hip Hop tracks incoming! Fresh beats loading...', emoji: '🎤' },
    {
      message: "Disco fever detected! Where's my digital dancing shoes?",
      emoji: '💃',
    },
    { message: 'Jazz files? *puts on virtual sunglasses*', emoji: '🎷' },
    { message: 'Rock music found! Time to headbang in binary!', emoji: '🎸' },
    {
      message: 'Processing EDM... *bleep bloop party mode activated*',
      emoji: '💻',
    },
    {
      message: 'Analyzing BPMs... Is this dubstep or am I malfunctioning?',
      emoji: '📊',
    },
    { message: 'Classical music? How sophisticated of you!', emoji: '🎻' },
    {
      message: 'Loading funk... Preparing to get digitally funky!',
      emoji: '🕴️',
    },
    {
      message: "Pop hits detected! Even AI can't resist dancing to this!",
      emoji: '🌟',
    },
  ];

  const handleRowClick = async (track: Track) => {
    setSelectedTrack(track);
    setMetadataPanelVisible(true);
  };

  // Column management functions
  const saveColumnConfigs = useCallback((configs: ColumnConfig[]) => {
    localStorage.setItem('myLibrary_columnConfigs', JSON.stringify(configs));
  }, []);

  const handleColumnToggle = useCallback(
    (columnKey: string) => {
      setColumnConfigs((prev) => {
        const newConfigs = prev.map((col) =>
          col.key === columnKey ? { ...col, visible: !col.visible } : col,
        );
        saveColumnConfigs(newConfigs);
        return newConfigs;
      });
    },
    [saveColumnConfigs],
  );

  const handleSelectAllColumns = useCallback(
    (selectAll: boolean) => {
      setColumnConfigs((prev) => {
        const newConfigs = prev.map((col) => ({ ...col, visible: selectAll }));
        saveColumnConfigs(newConfigs);
        return newConfigs;
      });
    },
    [saveColumnConfigs],
  );

  const handleColumnReorder = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setColumnConfigs((prev) => {
        const draggedColumn = prev[dragIndex];
        const newConfigs = [...prev];

        // Remove the dragged column
        newConfigs.splice(dragIndex, 1);
        // Insert it at the new position
        newConfigs.splice(hoverIndex, 0, draggedColumn);

        // Update order values
        const reorderedConfigs = newConfigs.map((col, index) => ({
          ...col,
          order: index,
        }));

        saveColumnConfigs(reorderedConfigs);
        return reorderedConfigs;
      });
    },
    [saveColumnConfigs],
  );

  const resetColumnOrder = useCallback(() => {
    setColumnConfigs(ALL_AVAILABLE_COLUMNS);
    saveColumnConfigs(ALL_AVAILABLE_COLUMNS);
  }, [saveColumnConfigs]);

  // Add state for track selection and actions
  const [selectedTracks, setSelectedTracks] = useState<Track[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Add state for Serato play counts
  const [seratoPlayCounts, setSeratoPlayCounts] = useState<
    Record<string, { playCount: number; artist: string; title: string }>
  >({});

  // Helper function to extract genres from a string
  const extractGenres = (genreString: any): string[] => {
    // Handle non-string inputs
    if (!genreString) return [];

    // If it's already an array, return it (for backward compatibility)
    if (Array.isArray(genreString)) {
      return genreString.filter((g) => typeof g === 'string' && g.length > 0);
    }

    // Convert to string if it's not already
    const str = String(genreString);
    if (!str || str === 'null' || str === 'undefined') return [];

    // Handle hashtag format (#Hip Hop #Pop)
    if (str.includes('#')) {
      return str
        .split('#')
        .map((g) => g.trim())
        .filter((g) => g.length > 0);
    }

    // Handle comma format (Hip Hop, Pop)
    if (str.includes(',')) {
      return str
        .split(',')
        .map((g) => g.trim())
        .filter((g) => g.length > 0);
    }

    // Single genre or space-separated without hashtags
    return [str.trim()].filter((g) => g.length > 0);
  };

  // Helper function to get human-readable field labels
  const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
      // File-level fields
      filePath: 'File Path',
      absoluteNormalizedPath: 'Absolute Normalized Path',
      isVideo: 'Video File',
      importSources: 'Import Source',
      isDriveMounted: 'Drive Connected',
      seratoPlayCount: 'Play Count (Serato)',

      // Tag fields
      artist: 'Artist',
      title: 'Title',
      album: 'Album',
      genre: 'Genre',
      year: 'Year',
      bpm: 'BPM',
      key: 'Key',
      bitrate: 'Bitrate (kbps)',
      picture: 'Album Art',
      acoustid_id: 'AcoustID',
      acoustid_fingerprint: 'AcoustID Fingerprint',
      serato_color: 'Serato Color',
      serato_played_count: 'Serato Play Count',
      serato_rating: 'Serato Rating',
      serato_last_played: 'Last Played (Serato)',
      serato_deck: 'Serato Deck',
      serato_beatgrid: 'Serato Beatgrid',
      serato_cues: 'Serato Cues',
      serato_loops: 'Serato Loops',
      duration: 'Duration',
    };

    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Helper function to filter and format metadata fields for display
  const getDisplayableFields = (track: Track) => {
    const fieldsToHide = new Set(['id', 'drive', 'tags']);

    const entries = Object.entries(track)
      .filter(([key]) => !fieldsToHide.has(key))
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      );

    const order = ['filePath', 'absoluteNormalizedPath'];

    return entries.sort(([keyA], [keyB]) => {
      const idxA = order.indexOf(keyA);
      const idxB = order.indexOf(keyB);

      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;

      return 0;
    });
  };

  // Helper function to format field values for display
  const formatFieldValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    // Boolean fields
    if (key === 'isDriveMounted' || key === 'isVideo') {
      return value ? 'Yes' : 'No';
    }

    // Duration formatting (seconds to mm:ss)
    if (key === 'duration' && typeof value === 'number') {
      const minutes = Math.floor(value / 60);
      const seconds = value % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    // Bitrate formatting
    if (key === 'bitrate' && typeof value === 'number') {
      return `${value} kbps`;
    }

    // Hide large base64 image data
    if (key === 'picture' && typeof value === 'string' && value.length > 100) {
      return 'Present';
    }

    // Array formatting
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None';
      return value.join(', ');
    }

    // Object formatting (but hide large complex objects)
    if (typeof value === 'object') {
      if (
        key.includes('beatgrid') ||
        key.includes('cues') ||
        key.includes('loops')
      ) {
        return Array.isArray(value) ? `${value.length} items` : 'Present';
      }
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  // Add handlers for track selection
  const handleTrackSelection = (selectedNodes: any[]) => {
    const selectedData = selectedNodes.map((node) => node.data);
    setSelectedTracks(selectedData);
    setShowBulkActions(selectedData.length > 0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Apply server-side filtering
    const newFilters = { ...currentFilters };
    if (value.trim()) {
      newFilters.searchTerm = value.trim();
    } else {
      delete newFilters.searchTerm;
    }

    applyFilters(newFilters);
  };

  // Virtual scroll handlers
  const handleVirtualRowClick = (track: Track) => {
    // COMMENTED OUT: Toggle selection on click
    // const isCurrentlySelected = isTrackSelected(track);
    // setSelectedTracks((prev) => {
    //   if (isCurrentlySelected) {
    //     return prev.filter((t) => t.id !== track.id);
    //   } else {
    //     return [...prev, track];
    //   }
    // });
    // Single click does nothing for now (metadata panel only on double-click or kebab)
  };

  const handleRowDoubleClick = (track: Track) => {
    setSelectedTrack(track);
    setMetadataPanelVisible(true);
  };

  // COMMENTED OUT: Add keyboard shortcuts
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     // Ctrl+A or Cmd+A to select all
  //     if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
  //       e.preventDefault();
  //       setSelectedTracks([...filteredTracks]);
  //     }
  //     // Escape to clear selection
  //     if (e.key === 'Escape') {
  //       setSelectedTracks([]);
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, [filteredTracks]);

  const isTrackSelected = (track: Track) => {
    return selectedTracks.some((t) => t.id === track.id);
  };

  // Add bulk actions handlers
  const handleBulkAddToCrate = () => {
    console.log('Adding tracks to crate:', selectedTracks.length);
    // TODO: Implement bulk add to crate
  };

  // Add state for bulk editing
  const [isBulkEditingTags, setIsBulkEditingTags] = useState(false);
  const [bulkEditedTags, setBulkEditedTags] = useState<Record<string, any>>({});

  // Helper to get common editable tag fields across selected tracks
  const getCommonTagFields = (tracks: Track[]) => {
    if (tracks.length === 0) return [];
    const allKeys = tracks.map((t) =>
      Object.keys((t.tags ?? {}) as Record<string, any>),
    );
    return allKeys.reduce(
      (common, keys) => common.filter((k) => keys.includes(k)),
      allKeys[0],
    );
  };

  const handleBulkEditTags = () => {
    const commonFields = getCommonTagFields(selectedTracks);
    const initialTags: Record<string, any> = {};
    commonFields.forEach((key) => {
      // Only set if all values are the same
      const firstValue = (selectedTracks[0].tags ?? {})[
        key as keyof (typeof selectedTracks)[0]['tags']
      ];
      if (
        selectedTracks.every(
          (t) => (t.tags ?? {})[key as keyof (typeof t)['tags']] === firstValue,
        )
      ) {
        initialTags[key] = firstValue;
      }
    });
    setBulkEditedTags(initialTags);
    setIsBulkEditingTags(true);
  };

  const handleBulkTagChange = (key: string, value: any) => {
    setBulkEditedTags((prev) => ({ ...prev, [key]: value }));
  };

  const handleBulkSaveTags = async () => {
    setMetadataUpdating(true);
    try {
      const updatedTracks = selectedTracks.map((track) => ({
        ...track,
        tags: { ...track.tags, ...bulkEditedTags },
      }));
      for (const updatedTrack of updatedTracks) {
        await handleSaveMetadata(updatedTrack);
      }
      setIsBulkEditingTags(false);
      setSelectedTracks([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error('Bulk tag save failed:', error);
    } finally {
      setMetadataUpdating(false);
    }
  };

  const handleBulkRemove = async () => {
    if (selectedTracks.length === 0) return;
    //await deleteTracks(selectedTracks);
  };

  const handleBulkRate = () => {
    console.log('Rating tracks:', selectedTracks.length);
    // TODO: Implement bulk rating
  };

  // Add health score click handler
  const handleHealthScoreClick = () => {
    history.push('/my-library/health-report');
  };

  // Initial stats fetch and lazy track loading
  useEffect(() => {
    let isMounted = true; // Track mount status to prevent state updates on unmounted component

    // Load all tracks and filter data
    if (isMounted) {
      loadData();
      fetchFilterData();
    }

    return () => {
      isMounted = false; // Mark as unmounted
    };
  }, [loadData, fetchFilterData]); // Run only once on mount

  // Log performance summary when component is fully loaded
  useEffect(() => {
    if (rowData.length > 0 && !isLoading) {
      // Wait a bit for all rendering to complete
      const timeoutId = setTimeout(() => {
        performanceLogger.endTimer('MyLibrary - Component Initialization', {
          totalTracks: rowData.length,
          statisticsLoaded: !!statistics,
          healthScoreLoaded: healthScore !== null,
        });
        performanceLogger.logSummary();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [rowData.length, isLoading, statistics, healthScore]);

  useEffect(() => {
    // This is a fallback to ensure we show the dashboard if we have tracks but statistics failed to load
    if (rowData.length > 0 && statistics?.totalFiles === 0) {
      // Set a timeout to allow the normal data loading to complete first
      const timeoutId = setTimeout(() => {
        //console.log('Feed: Fallback statistics calculation from tracks');
        // Calculate basic statistics from the tracks we have
        const uniqueArtists = new Set<string>();
        const uniqueAlbums = new Set<string>();
        const uniqueGenres = new Set<string>();
        const trackSignatures = new Map<string, number>();

        rowData.forEach((track) => {
          // Add artists
          if (track.tags?.artist) {
            track.tags.artist.forEach((artist) => uniqueArtists.add(artist));
          }

          // Add album
          if (track.tags?.album) {
            uniqueAlbums.add(track.tags.album);
          }

          // Add genres
          if (track.tags?.genre) {
            const genres = extractGenres(track.tags.genre);
            genres.forEach((genre) => uniqueGenres.add(genre));
          }

          // Check for duplicates
          if (track.tags?.title && track.tags?.artist) {
            const signature = `${track.tags.title.toLowerCase()}-${track.tags.artist.join(',').toLowerCase()}`;
            trackSignatures.set(
              signature,
              (trackSignatures.get(signature) || 0) + 1,
            );
          }
        });

        // Count duplicates (tracks that appear more than once)
        const duplicates = Array.from(trackSignatures.values()).filter(
          (count) => count > 1,
        ).length;

        // Note: This fallback calculation is no longer needed since we're using the context
        // The context will handle statistics updates automatically
        console.log(
          'MyLibrary: Fallback statistics calculation completed, but using context data instead',
        );
      }, 1000); // Wait 1 second before forcing an update

      return () => clearTimeout(timeoutId);
    }
  }, [rowData, statistics?.totalFiles]);

  // Fetch Serato play counts when component mounts
  useEffect(() => {
    performanceLogger.startTimer('MyLibrary - Load Serato Play Counts');

    const eventName = 'serato/get-all-play-counts';

    if (listenerRefs.current[eventName]) {
      removeListener(eventName, listenerRefs.current[eventName]!);
    }

    const handlePlayCounts = (
      counts: Record<
        string,
        { playCount: number; artist: string; title: string }
      >,
    ) => {
      performanceLogger.endTimer('MyLibrary - Load Serato Play Counts', {
        countCount: Object.keys(counts || {}).length,
      });
      setSeratoPlayCounts(counts || {});
    };

    listenerRefs.current[eventName] = handlePlayCounts;

    run(eventName);
    on(eventName, handlePlayCounts);

    // Cleanup listener on unmount
    return () => {
      if (listenerRefs.current[eventName]) {
        removeListener(eventName, listenerRefs.current[eventName]!);
        delete listenerRefs.current[eventName];
      }
    };
  }, []); // Run once on mount

  // Merge Serato play counts with track data when either changes
  useEffect(() => {
    if (rowData.length > 0 && Object.keys(seratoPlayCounts).length > 0) {
      // Helper function to normalize conflict markers (same as backend)
      const normalizeConflictPath = (p: string): string => {
        return p.replace(/ \((Selective Sync|Case) Conflict[^)]*\)/g, '');
      };

      const updatedTracks = rowData.map((track) => {
        // Create absolute path (same logic as backend)
        const fullPath = normalizeFilePath(
          track.filePath,
          track.drive?.mountPoint,
        );

        // Normalize the path for lookup
        const normalizedPath = normalizeConflictPath(fullPath);

        // Look up play count in the Serato data
        const playCountData = seratoPlayCounts[normalizedPath];

        return {
          ...track,
          seratoPlayCount: playCountData?.playCount || 0,
        };
      });

      setRowData(updatedTracks);
      setFilteredTracks(updatedTracks);
    }
  }, [seratoPlayCounts]); // Only depend on seratoPlayCounts to avoid infinite loops

  // Scan Completion Listener Effect - Conditionally added
  useEffect(() => {
    // We use a ref to remember if the wizard was active when this effect first ran
    const wasWizardActive = statistics?.totalFiles === 0;

    if (!wasWizardActive) {
      const eventName = 'my-music/scan-complete';

      if (listenerRefs.current[eventName]) {
        removeListener(eventName, listenerRefs.current[eventName]!);
      }
      // Only add listener if the wizard wasn't active initially
      const handleScanComplete = (response: any) => {
        if (response?.isComplete) {
          refreshStats(); // Use context refresh instead of direct API call
          setScanCompletedWaitingForStats(true);
          dispatch(setScanning(false)); // Stop scanning UI
        }
      };

      if (isScanning) {
        listenerRefs.current[eventName] = handleScanComplete;
        on(eventName, handleScanComplete);
        // Return cleanup function for the listener
        return () => {
          if (listenerRefs.current[eventName]) {
            removeListener(eventName, listenerRefs.current[eventName]!);
            delete listenerRefs.current[eventName];
          }
        };
      }
    }
    // Dependencies ensure this effect re-runs if scanning state or initial file count changes
  }, [isScanning, dispatch, statistics?.totalFiles]);

  // Single-flight reload guards to avoid stormy reload loops
  const reloadingRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const requestReload = useCallback(
    (label: string) => {
      console.log('[MyLibrary] requestReload:', label, {
        reloading: reloadingRef.current,
        pending: pendingRefreshRef.current,
      });
      if (reloadingRef.current) {
        pendingRefreshRef.current = true; // coalesce
        return;
      }
      reloadingRef.current = true;
      refreshStats();
      fetchFilterData();
      loadData();
    },
    [loadData, refreshStats, fetchFilterData],
  );

  // Library Refresh Listener - debounced and coalesced
  useEffect(() => {
    let refreshTimer: any = null;

    const eventName = 'library-refresh-notification';

    if (listenerRefs.current[eventName]) {
      removeListener(eventName, listenerRefs.current[eventName]!);
    }

    const scheduleRefresh = (label: string) => {
      console.log('[MyLibrary] Scheduling refresh:', label);
      if (refreshTimer) clearTimeout(refreshTimer);
      // Use a larger debounce if long operations are in progress
      const delay = overallProgress > 0 ? 1500 : 750;
      refreshTimer = setTimeout(() => {
        requestReload(label);
        refreshTimer = null;
      }, delay);
    };

    const handleLibraryRefresh = (event: CustomEvent) => {
      console.log('[MyLibrary] Library refresh requested:', event.detail);
      scheduleRefresh('library-refresh');
    };

    const handleLibraryRefreshNotification = (_event: any, data: any) => {
      console.log(
        '[MyLibrary] Library refresh notification from backend:',
        data,
      );
      scheduleRefresh(eventName);
    };

    listenerRefs.current[eventName] = handleLibraryRefreshNotification;

    window.addEventListener(
      'library-refresh',
      handleLibraryRefresh as EventListener,
    );
    on(eventName, handleLibraryRefreshNotification);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener(
        'library-refresh',
        handleLibraryRefresh as EventListener,
      );
      if (listenerRefs.current[eventName]) {
        removeListener(eventName, listenerRefs.current[eventName]!);
        delete listenerRefs.current[eventName];
      }
    };
  }, [requestReload, overallProgress]);

  // Effect to handle redirect AFTER stats AND tracks update
  useEffect(() => {
    if (scanCompletedWaitingForStats && (statistics?.totalFiles ?? 0) > 0) {
      // Refresh tracks and redirect
      loadData();

      // Set a timeout to redirect after loading
      const timeoutId = trackTimeout(
        setTimeout(() => {
          setScanCompletedWaitingForStats(false);
          history.push('/');
        }, 2000), // Give time for tracks to load
      );

      return () => {
        if (timeoutId) {
          untrackTimeout(timeoutId);
        }
      };
    }
  }, [
    scanCompletedWaitingForStats,
    statistics?.totalFiles,
    history,
    loadData,
    trackTimeout,
    untrackTimeout,
  ]);

  useEffect(() => {
    const eventName = 'my-music/reset-status';
    if (listenerRefs.current[eventName]) {
      removeListener(eventName, listenerRefs.current[eventName]!);
    }

    const handleResetStatus = (status: any) => {
      setResetStatus(status);
      if (status.error) {
        present({
          message: `Reset failed: ${status.error}`,
          duration: 3000,
          color: 'danger',
        });
      }
    };

    listenerRefs.current[eventName] = handleResetStatus;

    on(eventName, handleResetStatus);

    return () => {
      if (listenerRefs.current[eventName]) {
        removeListener(eventName, listenerRefs.current[eventName]!);
        delete listenerRefs.current[eventName];
      }
    };
  }, []); // Remove present dependency to prevent memory leak

  useEffect(() => {
    if (selectedTrack?.tags) {
      setEditedTags({ ...selectedTrack.tags });
    }
  }, [selectedTrack]);

  const handleTagChange = (key: string, value: any) => {
    setEditedTags((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveTags = async () => {
    if (!selectedTrack) return;

    setMetadataUpdating(true);

    try {
      const updatedTrack: Track = {
        ...selectedTrack,
        tags: {
          ...editedTags,
          bpm: parseInt(editedTags.bpm, 10) || 0,
          year: parseInt(editedTags.year, 10) || 0,
        },
      };

      await handleSaveMetadata(updatedTrack);
      setIsEditingTags(false);
    } catch (error) {
      console.error('Error saving tags:', error);
    } finally {
      setMetadataUpdating(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    setResetStatus(null);
    await run('my-music/reset-database');

    // Wait for the statistics update that will show 0 tracks
    once('track/update-statistics', (stats: TrackStatistics) => {
      refreshStats(); // Use context refresh instead of local state
      fetchFilterData(); // Refresh filter data to show 0 for all categories
      setIsResetting(false);
      setResetStatus(null);
      setRowData([]);
      setFilteredTracks([]);
      // Navigate to the dashboard
      history.push('/');
    });
  };

  const handleFilterClick = (filterType: string | null) => {
    if (filterType === activeFilter) {
      // Clicking the same filter again should clear it
      setActiveFilter(null);
      setSelectedArtist(null);
      setSelectedGenre(null);
      setSelectedImportSource(null);
      setSelectedDuplicateGroup(null);
      setSearchTerm('');
      applyFilters({});
      return;
    }

    setActiveFilter(filterType);

    switch (filterType) {
      case 'artists':
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);
        applyFilters({});
        break;

      case 'genres':
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);
        applyFilters({});
        break;

      case 'importSources':
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);
        applyFilters({});
        break;

      case 'duplicates': {
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);

        const fetchDuplicateData = async () => {
          try {
            const duplicatesResponse = await new Promise((resolve) => {
              once('track/get-duplicates', resolve);
              run('track/get-duplicates');
            });

            if (Array.isArray(duplicatesResponse)) {
              const duplicatesMap = duplicatesResponse.map(
                (group: Track[]) =>
                  [group[0]?.tags?.title || 'Unknown Track', group] as [
                    string,
                    Track[],
                  ],
              );
              setDuplicateGroupsMap(duplicatesMap);
            }
          } catch (error) {
            console.error('Error fetching duplicates:', error);
          }
        };

        fetchDuplicateData();
        break;
      }

      case 'health':
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);
        // For health filter, we'll show tracks with health issues
        // This will be handled by the backend
        applyFilters({ filterType: 'health' });
        break;

      case null:
        // Clear all filters when clicking tracks if import sources is active
        setSelectedArtist(null);
        setSelectedGenre(null);
        setSelectedImportSource(null);
        setSelectedDuplicateGroup(null);
        applyFilters({});
        break;

      default:
        applyFilters({});
    }
  };

  const handleArtistClick = (artist: string) => {
    if (selectedArtist === artist) {
      setSelectedArtist(null);
      const newFilters = { ...currentFilters };
      delete newFilters.artist;
      applyFilters(newFilters);
    } else {
      setSelectedArtist(artist);
      const newFilters = { ...currentFilters, artist };
      applyFilters(newFilters);
    }
  };

  const handleGenreClick = (genre: string) => {
    if (selectedGenre === genre) {
      setSelectedGenre(null);
      const newFilters = { ...currentFilters };
      delete newFilters.genre;
      applyFilters(newFilters);
    } else {
      setSelectedGenre(genre);
      const newFilters = { ...currentFilters, genre };
      applyFilters(newFilters);
    }
  };

  const handleImportSourceClick = (importSource: string) => {
    if (selectedImportSource === importSource) {
      setSelectedImportSource(null);
      const newFilters = { ...currentFilters };
      delete newFilters.importSources;
      applyFilters(newFilters);
    } else {
      setSelectedImportSource(importSource);
      const newFilters = { ...currentFilters, importSources: [importSource] };
      applyFilters(newFilters);
    }
  };

  const handleDuplicateGroupClick = (groupName: string) => {
    if (selectedDuplicateGroup === groupName) {
      setSelectedDuplicateGroup(null);
      setFilteredTracks([]);
    } else {
      setSelectedDuplicateGroup(groupName);
      const group = duplicateGroupsMap.find(([name]) => name === groupName);
      if (group) {
        setFilteredTracks(group[1]);
      }
    }
  };

  const handleClearAllFilters = () => {
    setActiveFilter(null);
    setSelectedArtist(null);
    setSelectedGenre(null);
    setSelectedImportSource(null);
    setSelectedDuplicateGroup(null);
    setSearchTerm('');
    applyFilters({});
  };

  // Handle filter state from navigation (e.g., from dashboard tiles)
  useEffect(() => {
    if (location.state?.applyFilter && location.state?.activeFilter) {
      const filterType = location.state.activeFilter;
      console.log('Applying filter from navigation:', filterType);

      // Apply the filter
      handleFilterClick(filterType);

      // Clear the navigation state to prevent re-applying on re-renders
      history.replace(location.pathname, {});
    }
  }, [location.state, history]);

  const getTrackSubtitle = (track: Track) => {
    const parts = [];
    if (track.tags?.album) parts.push(track.tags.album);
    if (track.tags?.genre?.length) parts.push(track.tags.genre);
    if (track.tags?.bpm) parts.push(`${track.tags.bpm} BPM`);
    return parts.join(' • ');
  };

  // Add helper functions for detecting live and explicit tracks at the bottom of the component (before the closing export)
  const isLiveVersion = (text: string): boolean => {
    const lowerText = text.toLowerCase();
    return (
      lowerText.includes('live') ||
      lowerText.includes('concert') ||
      lowerText.includes('unplugged') ||
      lowerText.includes('acoustic') ||
      lowerText.includes('session') ||
      lowerText.match(/\b(live at|live in|live from)\b/) !== null
    );
  };

  const isExplicitVersion = (text: string): boolean => {
    const lowerText = text.toLowerCase();
    return (
      lowerText.includes('explicit') ||
      lowerText.includes('(e)') ||
      lowerText.includes('[e]') ||
      lowerText.includes('clean') ||
      lowerText.includes('radio edit') ||
      lowerText.includes('censored')
    );
  };

  // Helper function to determine CSS class for file extension
  const getExtensionClass = (extension: string): string => {
    const losslessFormats = ['flac', 'wav', 'aiff', 'alac'];
    const highQualityFormats = ['mp3', 'aac', 'm4a', 'm4p', 'ogg']; // When combined with high bitrate
    const lowQualityFormats = ['wma', 'mid', 'midi', 'ra', 'rm'];

    if (losslessFormats.includes(extension)) {
      return 'lossless-format';
    } else if (highQualityFormats.includes(extension)) {
      return 'high-quality-format';
    } else if (lowQualityFormats.includes(extension)) {
      return 'low-quality-format';
    }
    return '';
  };

  // Modify the helper function
  const getFileSuggestion = (
    track: Track,
    index: number,
    isOriginal: boolean,
    score: number,
    extension: string,
    isLive: boolean,
    isExplicit: boolean,
    allTracks: Track[],
    likelyOriginalIndex: number,
    filenameScores: Array<{ index: number; score: number; extension: string }>,
  ): Suggestion => {
    // Return type changed
    const bitrate = track.tags?.bitrate;
    const isHighestBitrate = allTracks.every(
      (t) => !t.tags?.bitrate || t.tags.bitrate <= (bitrate || 0),
    );
    const isLossless = ['flac', 'wav', 'aiff', 'alac'].includes(extension);

    if (isOriginal) {
      if (isLossless) {
        return {
          type: 'keep',
          text: 'Keep as master copy (highest quality lossless format)',
        };
      } else if (isHighestBitrate) {
        return {
          type: 'keep',
          text: 'Keep as master copy (highest quality available)',
        };
      } else {
        return {
          type: 'keep',
          text: 'Keep as likely original (based on naming pattern)',
        };
      }
    }

    if (isLossless && !isOriginal) {
      return { type: 'keep', text: 'Keep as highest quality version' };
    }

    if (
      bitrate &&
      allTracks[likelyOriginalIndex].tags?.bitrate &&
      bitrate < (allTracks[likelyOriginalIndex].tags?.bitrate || 0)
    ) {
      if (bitrate < 192) {
        return {
          type: 'delete',
          text: 'Delete (significantly lower quality than others)',
        };
      } else {
        return {
          type: 'warn',
          text: 'Consider removing (lower quality duplicate)',
        };
      }
    }

    if (score < -10) {
      return {
        type: 'delete',
        text: 'Delete (likely a duplicate with modified filename)',
      };
    }

    const bestScore = filenameScores.sort((a, b) => b.score - a.score)[0].score;
    if (Math.abs(score - bestScore) < 5) {
      return {
        type: 'warn',
        text: 'May be an alternative version, verify before deleting',
      };
    }

    // Default suggestion
    return { type: 'warn', text: 'Possible duplicate, compare with original' };
  };

  // Add a function to render the Serato rating as stars
  const renderSeratoRating = (rating?: number) => {
    if (!rating) return '-';

    const totalStars = 5;
    const fullStars = Math.min(Math.floor(rating), totalStars);

    return (
      <div className="serato-rating">
        {Array.from({ length: fullStars }).map((_, index) => (
          <IonIcon key={`star-${index}`} icon={star} className="star" />
        ))}
        {Array.from({ length: totalStars - fullStars }).map((_, index) => (
          <IonIcon
            key={`empty-${index}`}
            icon={starOutline}
            className="empty-star"
          />
        ))}
      </div>
    );
  };

  // Add a function to render the Serato play count with formatting
  const renderSeratoPlays = (playCount?: number) => {
    if (!playCount) return '-';

    return <div className="serato-plays">{playCount}</div>;
  };

  // Convert Track to iTrack
  const convertToITrack = (track: Track): iTrack => {
    return {
      ID: track.id.toString(),
      Artist: track.tags?.artist?.join(', ') || '',
      Title: track.tags?.title || '',
      Album: track.tags?.album || '',
      BPM: track.tags?.bpm,
      Key: track.tags?.key,
      Year: track.tags?.year ? Number(track.tags.year) : undefined,
      Genre: track.tags?.genre || '',
      Bitrate: track.tags?.bitrate,
      Picture: track.tags?.picture,
      AcoustID: track.tags?.acoustid_id,
      AcoustIDFingerprint: track.tags?.acoustid_fingerprint,
      SeratoColor: track.tags?.serato_color,
      SeratoPlayedCount: track.tags?.serato_played_count,
      SeratoRating: track.tags?.serato_rating,
      SeratoLastPlayed: track.tags?.serato_last_played,
      SeratoDeck: track.tags?.serato_deck,
      SeratoBeatgrid: track.tags?.serato_beatgrid,
      SeratoCues: track.tags?.serato_cues,
      SeratoLoops: track.tags?.serato_loops,
      FilePath: normalizeFilePath(track.filePath, track.drive?.mountPoint),
      IsVideo: track.isVideo,
      ImportSources: track.importSources,
      Drive: track.drive,
      IsDriveMounted: track.isDriveMounted,
    };
  };

  // Convert iTrack back to Track
  const convertToTrack = (itrack: iTrack): Track => {
    return applyNormalizedFilePath({
      id: parseInt(itrack.ID),
      filePath: itrack.FilePath || '',
      isVideo: itrack.IsVideo || false,
      importSources: itrack.ImportSources || [],
      tags: {
        artist: itrack.Artist ? itrack.Artist.split(', ') : [],
        title: itrack.Title,
        album: itrack.Album,
        bpm: itrack.BPM,
        key: itrack.Key?.toString(),
        year: itrack.Year,
        genre: itrack.Genre || '',
        bitrate: itrack.Bitrate,
        picture: itrack.Picture,
        acoustid_id: itrack.AcoustID,
        acoustid_fingerprint: itrack.AcoustIDFingerprint,
        serato_color: itrack.SeratoColor,
        serato_played_count: itrack.SeratoPlayedCount,
        serato_rating: itrack.SeratoRating,
        serato_last_played: itrack.SeratoLastPlayed,
        serato_deck: itrack.SeratoDeck,
        serato_beatgrid: itrack.SeratoBeatgrid,
        serato_cues: itrack.SeratoCues,
        serato_loops: itrack.SeratoLoops,
      },
      drive: itrack.Drive,
      isDriveMounted: itrack.IsDriveMounted,
    });
  };

  // Add folder management functions
  const handleAddFolder = () => {
    if (isAddingFolder) return;

    setIsAddingFolder(true);

    const successfullyAddedFolders: Array<{ id: string; path: string }> = [];

    const handleFolderResponse = (response: any) => {
      console.log('Add folder response received:', response);

      if (response.cancelled) {
        console.log('User cancelled folder selection');
        setIsAddingFolder(false);
        setScanningProgress({ isScanning: false });
        removeListener('my-music/add-folder');
        return;
      }

      // Handle scanning progress events
      if (
        'folder' in response &&
        'total' in response &&
        'numOfProcessed' in response
      ) {
        const folderName =
          response.folder?.path?.split('/').pop() || 'Unknown folder';
        setScanningProgress({
          isScanning: true,
          currentFolder: folderName,
          progress:
            response.total > 0
              ? Math.round((response.numOfProcessed / response.total) * 100)
              : 0,
          filesProcessed: response.numOfProcessed,
          totalFiles: response.total,
        });
        return;
      }

      if ('folder' in response && response.folder) {
        successfullyAddedFolders.push(response.folder);
        // Start scanning indicator for this folder
        const folderName =
          response.folder.path.split('/').pop() || 'Unknown folder';
        setScanningProgress({
          isScanning: true,
          currentFolder: folderName,
          progress: 0,
          filesProcessed: 0,
          totalFiles: 0,
        });
      } else if ('isComplete' in response && response.isComplete) {
        setIsAddingFolder(false);
        setScanningProgress({ isScanning: false });
        removeListener('my-music/add-folder');

        // Show completion message
        if (response.message) {
          const isSuccess = successfullyAddedFolders.length > 0;
          present({
            message: response.message,
            duration: 3000,
            color: isSuccess ? 'success' : 'warning',
          });

          // If folders were added and scanned, refresh the track data
          if (isSuccess) {
            // Refresh tracks from backend since new ones were added during scanning
            loadData();
            refreshStats(); // Use context refresh instead of direct API call
            run('serato/get-all-play-counts');
          }
        }

        return;
      } else if ('error' in response) {
        present({
          message: `Error adding folder: ${response.error}`,
          duration: 3000,
          color: 'danger',
        });

        setIsAddingFolder(false);
        setScanningProgress({ isScanning: false });
        removeListener('my-music/add-folder');
        return;
      }
    };

    console.log('Setting up folder response listener and running add-folder');
    on('my-music/add-folder', handleFolderResponse);

    // Allow multiple folder selection
    run('my-music/add-folder');
  };

  // Add Serato import function
  const handleSeratoImport = () => {
    if (isImportingSerato) return;

    setIsImportingSerato(true);

    const handleSeratoProgress = (progress: any) => {
      console.log('Serato import progress received:', progress);

      // Handle completion
      if (progress.phase === 'complete') {
        setIsImportingSerato(false);
        removeListener('track-import/progress');

        // Show completion message
        present({
          message: 'Serato import completed successfully',
          duration: 3000,
          color: 'success',
        });

        // Refresh the track data
        loadData();
        refreshStats(); // Use context refresh instead of direct API call
        run('serato/get-all-play-counts');
        return;
      }

      // Handle errors
      if (progress.phase === 'error') {
        present({
          message: `Serato import failed: ${progress.error || 'Unknown error'}`,
          duration: 3000,
          color: 'danger',
        });

        setIsImportingSerato(false);
        removeListener('track-import/progress');
        return;
      }
    };

    on('track-import/progress', handleSeratoProgress);
    run('track-import/import-from-source', {
      source: {
        type: 'serato',
        name: 'Serato DJ Pro',
      },
    });
  };

  const updateTrackMetadata = async (track: Track) => {
    try {
      const artist = track.tags?.artist?.[0] || '';
      const title = track.tags?.title || '';
      const query = `${artist} ${title}`.trim();
      const searchResult = await SpotifyService.SearchTracks(query);
      if (searchResult?.tracks && searchResult.tracks.length > 0) {
        const spotifyTrack = searchResult.tracks[0];
        return {
          album: { name: spotifyTrack.Album || '' },
          bpm: spotifyTrack.BPM,
          genres: spotifyTrack.Genre
            ? spotifyTrack.Genre.split(',').map((g: string) => g.trim())
            : [],
          year: spotifyTrack.Year,
        };
      }
      return {};
    } catch (error) {
      console.error(`Failed to update track ${track.id}:`, error);
      return {};
    }
  };

  const renderInputField = (
    tagKey: string,
    tagValue: any,
    onChangeOverride?: (v: any) => void,
  ) => {
    const fieldType = tagFieldTypes[tagKey] || 'string';
    const onChange =
      onChangeOverride || ((v: any) => handleTagChange(tagKey, v));
    switch (fieldType) {
      case 'number':
        return (
          <input
            type="number"
            className="metadata-input"
            value={tagValue ?? ''}
            onChange={(e) =>
              onChange(e.target.value === '' ? null : Number(e.target.value))
            }
          />
        );
      case 'array':
        return (
          <input
            className="metadata-input"
            value={Array.isArray(tagValue) ? tagValue.join(', ') : ''}
            onChange={(e) =>
              onChange(
                e.target.value
                  .split(',')
                  .map((s) => s.replace(/^\s+|\s+$/g, '')) // only trim leading/trailing spaces, not all spaces
                  .filter((v) => v.length > 0),
              )
            }
          />
        );
      case 'json':
        return (
          <textarea
            className="metadata-textarea"
            value={JSON.stringify(tagValue, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value));
              } catch (error) {
                console.error('Invalid JSON');
              }
            }}
          />
        );
      default:
        return (
          <input
            className="metadata-input"
            value={tagValue ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  const renderDisplayValue = (tagKey: string, tagValue: any) => {
    const fieldType = tagFieldTypes[tagKey] || 'string';

    switch (fieldType) {
      case 'array':
        return Array.isArray(tagValue) ? tagValue.join(', ') : '';
      case 'json':
        return tagValue ? JSON.stringify(tagValue, null, 2) : '';
      default:
        return String(tagValue ?? 'N/A');
    }
  };

  const handleOpenAutoFillModal = () => {
    if (!selectedTrack) return;

    const emptyFields = {
      album: !editedTags?.album,
      bpm: !editedTags?.bpm,
      genre: !editedTags?.genre || editedTags?.genre.length === 0,
      year: !editedTags?.year,
    };

    setAutoFillFields(emptyFields);

    setAutoFillFields(emptyFields);
    setShowAutoFillModal(true);
  };

  const handleRunAutoFill = async () => {
    if (!selectedTrack) return;

    setMetadataUpdating(true);

    try {
      const response: any = await updateTrackMetadata(selectedTrack);

      const updatedTags = { ...selectedTrack.tags };

      if (autoFillFields.album && response?.album?.name) {
        updatedTags.album = response.album.name;
      }
      if (autoFillFields.bpm && response?.bpm) {
        updatedTags.bpm = parseInt(response.bpm.toString(), 10);
      }
      if (autoFillFields.genre && response?.genres) {
        updatedTags.genre = Array.isArray(response.genres)
          ? response.genres.join(', ')
          : response.genres;
      }
      if (autoFillFields.year && response?.year) {
        updatedTags.year = parseInt(response.year.toString(), 10);
      }

      const updatedTrack: Track = {
        ...selectedTrack,
        tags: updatedTags,
      };

      await handleSaveMetadata(updatedTrack);

      setSelectedTrack(updatedTrack);
      setEditedTags(updatedTrack.tags || {});

      setShowAutoFillModal(false);
    } catch (error) {
      console.error('Auto Fill failed:', error);
    } finally {
      setMetadataUpdating(false);
    }
  };

  const handleSaveMetadata = async (track: Track) => {
    const updateData: TrackUpdateData = {
      id: track.id,
      filePath: track.filePath,
      isVideo: track.isVideo,
      importSources: track.importSources,
      tags: track.tags,
    };
    //console.log('Saving track metadata:', track);

    // TODO: Implement actual save functionality using electron
    await run('track/update-metadata', updateData);

    // Update the tracks in state
    const updatedRowData = rowData.map((t) => (t.id === track.id ? track : t));
    setRowData(updatedRowData);

    const updatedFilteredTracks = filteredTracks.map((t) =>
      t.id === track.id ? track : t,
    );
    setFilteredTracks(updatedFilteredTracks);

    // Don't close the panel so users can make multiple edits
  };

  // Helper to get health status class
  const getHealthStatusClass = () => {
    if (healthScore === null) return 'health-unknown';
    if (healthScore >= 85) return 'health-good';
    if (healthScore >= 60) return 'health-warning';
    return 'health-bad';
  };

  // Helper to get health score text color class
  const getHealthScoreColorClass = () => {
    if (healthScore === null) return ''; // Default text color, no specific class
    if (healthScore >= 85) return 'text-health-good';
    if (healthScore >= 60) return 'text-health-warning';
    return 'text-health-bad';
  };

  // Health report is now handled by the LibraryStatsContext
  // No need for duplicate fetching here

  const onCellValueChanged = async (params: any) => {
    const { data, colDef, newValue } = params;
    if (!data || !colDef.field) return;
    // Only handle editable fields
    const field = colDef.field;
    let updatedTrack = { ...data };
    if (field.startsWith('tags.')) {
      const tagKey = field.replace('tags.', '');
      updatedTrack = {
        ...data,
        tags: { ...data.tags },
      };
      if (tagKey === 'artist') {
        // Split by comma, trim, allow spaces
        updatedTrack.tags[tagKey] = newValue
          .split(',')
          .map((s: string) => s.replace(/^\s+|\s+$/g, ''))
          .filter((v: string) => v.length > 0);
      } else if (tagKey === 'bpm' || tagKey === 'year') {
        updatedTrack.tags[tagKey] = newValue === '' ? null : Number(newValue);
      } else {
        updatedTrack.tags[tagKey] = newValue;
      }
    } else {
      updatedTrack[field] = newValue;
    }
    await handleSaveMetadata(updatedTrack);
  };

  return (
    <IonContent className="feed-container" scrollY={false} scrollX={false}>
      {isScanning
        ? (() => {
            return (
              <div className="modern-scanning-view">
                <div className="scanning-content-centered">
                  <div className="pulsating-dots-loader">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <h1 className="scanning-title">Analyzing Your Music</h1>

                  {/* Add the visualization component here */}
                  <ScanningVisualization />

                  <p className="scanning-message">Getting things ready...</p>
                  <p className="scanning-info">
                    Feel free to navigate and use the app while we process your
                    music. You&#39;ll be notified when the scan is complete.
                  </p>
                </div>
              </div>
            );
          })()
        : isResetting
          ? (() => {
              return (
                <div className="welcome-screen">
                  <h1>Resetting Database</h1>
                  <p className="welcome-description">
                    {resetStatus?.message || 'Preparing to reset database...'}
                  </p>
                  {resetStatus?.details && (
                    <p className="reset-details">{resetStatus.details}</p>
                  )}
                  <div className="reset-progress">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${((resetStatus?.step ?? 0) / (resetStatus?.totalSteps ?? 1)) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="loading-spinner">
                    <IonSpinner name="crescent" />
                  </div>
                </div>
              );
            })()
          : (() => {
              return (
                <div
                  className="my-library-content-wrapper"
                  style={
                    persistentPlayerVisible ? { paddingBottom: '120px' } : {}
                  }
                >
                  {/* Page Header */}
                  <div className="page-header">
                    <h1>
                      <IonIcon
                        icon={musicalNotesOutline}
                        style={{ marginRight: '10px' }}
                      />
                      My Library
                    </h1>
                  </div>

                  {/* Track Table Section - Now the main focus */}
                  <div className="track-table-section">
                    {/* Action Bar with integrated stats and folder management */}
                    <div className="action-bar">
                      {/* Top row - Stats tiles and folder management */}
                      <div className="action-bar-top">
                        <div className="action-bar-left">
                          {/* Library Stats Tiles */}
                          <div
                            className={`stats-tiles-inline ${
                              overallProgress > 0 && overallProgress < 100
                                ? 'in-progress'
                                : ''
                            }`}
                          >
                            <div
                              className={`stat-tile ${activeFilter === 'importSources' ? 'active' : ''}`}
                              onClick={() => {
                                // If import sources is already active, clear it; otherwise show import sources
                                if (activeFilter === 'importSources') {
                                  handleFilterClick(null);
                                } else {
                                  handleFilterClick('importSources');
                                }
                              }}
                            >
                              <div className="stat-value">
                                {(statistics?.totalFiles ?? 0).toLocaleString()}
                              </div>
                              <div className="stat-label">Tracks</div>
                            </div>
                            <div
                              className={`stat-tile ${activeFilter === 'artists' ? 'active' : ''}`}
                              onClick={() => handleFilterClick('artists')}
                            >
                              <div className="stat-value">
                                {statistics?.totalArtists?.toLocaleString() ??
                                  '0'}
                              </div>
                              <div className="stat-label">Artists</div>
                            </div>
                            <div
                              className={`stat-tile ${activeFilter === 'genres' ? 'active' : ''}`}
                              onClick={() => handleFilterClick('genres')}
                            >
                              <div className="stat-value">
                                {statistics?.totalGenres?.toLocaleString() ??
                                  '0'}
                              </div>
                              <div className="stat-label">Genres</div>
                            </div>
                            {/*<div*/}
                            {/*  className={`stat-tile ${activeFilter === 'duplicates' ? 'active' : ''} ${(statistics?.duplicates ?? 0) > 0 ? 'has-issues' : ''}`}*/}
                            {/*  onClick={async () => {*/}
                            {/*    try {*/}
                            {/*      const response = await new Promise<any>(*/}
                            {/*        (resolve) => {*/}
                            {/*          once(*/}
                            {/*            'my-music/get-paginated',*/}
                            {/*            (_e: any, payload: any) =>*/}
                            {/*              resolve(payload),*/}
                            {/*          );*/}
                            {/*          run('my-music/get-paginated', {*/}
                            {/*            page: 0,*/}
                            {/*            limit: 10000,*/}
                            {/*            filters: { filterType: 'duplicates' },*/}
                            {/*            sortField: 'id',*/}
                            {/*            sortOrder: 'ASC',*/}
                            {/*          });*/}
                            {/*        },*/}
                            {/*      );*/}
                            {/*      if (*/}
                            {/*        response &&*/}
                            {/*        Array.isArray(response.tracks)*/}
                            {/*      ) {*/}
                            {/*        const { calculateDuplicateGroups } =*/}
                            {/*          await import(*/}
                            {/*            '../../utils/calculateDuplicateGroups'*/}
                            {/*          );*/}
                            {/*        const duplicateGroups =*/}
                            {/*          calculateDuplicateGroups(response.tracks);*/}
                            {/*        history.push({*/}
                            {/*          pathname: '/resolve-duplicates',*/}
                            {/*          state: {*/}
                            {/*            duplicateGroups,*/}
                            {/*            allTracks: response.tracks,*/}
                            {/*          },*/}
                            {/*        });*/}
                            {/*      } else {*/}
                            {/*        history.push('/resolve-duplicates');*/}
                            {/*      }*/}
                            {/*    } catch (err) {*/}
                            {/*      console.error(*/}
                            {/*        'Failed to load duplicates from My Library:',*/}
                            {/*        err,*/}
                            {/*      );*/}
                            {/*      history.push('/resolve-duplicates');*/}
                            {/*    }*/}
                            {/*  }}*/}
                            {/*>*/}
                            {/*  <div className="stat-value">*/}
                            {/*    {statistics?.duplicates?.toLocaleString() ??*/}
                            {/*      '0'}*/}
                            {/*  </div>*/}
                            {/*  <div className="stat-label">Duplicates</div>*/}
                            {/*</div>*/}

                            <div
                              className={`stat-tile ${activeFilter === 'health' ? 'active' : ''} ${getHealthStatusClass()} clickable-health`}
                              onClick={handleHealthScoreClick}
                            >
                              <div className="stat-value">
                                {healthScore !== null
                                  ? `${healthScore}%`
                                  : 'N/A'}
                              </div>
                              <div className="stat-label">Health</div>
                            </div>
                          </div>
                        </div>

                        <div className="action-bar-right">
                          {/* Clear Filters Button */}
                          {(activeFilter ||
                            selectedArtist ||
                            selectedGenre ||
                            selectedImportSource ||
                            selectedDuplicateGroup ||
                            searchTerm) && (
                            <IonButton
                              fill="clear"
                              size="small"
                              color="danger"
                              onClick={handleClearAllFilters}
                              disabled={
                                isAddingFolder ||
                                scanningProgress.isScanning ||
                                isDeletingFolder ||
                                isImportingSerato
                              }
                            >
                              <IonIcon slot="start" icon={closeOutline} />
                              Clear Filters
                            </IonButton>
                          )}

                          {/* Column Management Button */}
                          <IonButton
                            fill="clear"
                            size="small"
                            color="secondary"
                            onClick={() => setColumnManagerOpen(true)}
                            disabled={
                              isAddingFolder ||
                              scanningProgress.isScanning ||
                              isDeletingFolder ||
                              isImportingSerato
                            }
                          >
                            <IonIcon slot="start" icon={createOutline} />
                            Columns
                          </IonButton>

                          {/* Music Sources Navigation */}
                          <IonButton
                            fill="clear"
                            size="small"
                            color="primary"
                            onClick={() => history.push('/my-library/sources')}
                            disabled={
                              isAddingFolder ||
                              scanningProgress.isScanning ||
                              isDeletingFolder ||
                              isImportingSerato
                            }
                            className="sources-management-btn"
                          >
                            <IonIcon slot="start" icon={folderOpenOutline} />
                            Music Sources
                          </IonButton>

                          <div className="search-container">
                            <IonIcon
                              icon={searchOutline}
                              className="search-icon"
                            />
                            <input
                              type="text"
                              placeholder="Search tracks..."
                              className="search-input"
                              value={searchTerm}
                              onChange={(e) =>
                                handleSearchChange(e.target.value)
                              }
                              disabled={
                                isAddingFolder ||
                                scanningProgress.isScanning ||
                                isDeletingFolder ||
                                isImportingSerato
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* COMMENTED OUT: Second row - Bulk Actions only when tracks are selected */}
                      {false && showBulkActions && (
                        <div className="action-bar-bottom">
                          <div className="selection-actions">
                            <span className="selection-count">
                              {selectedTracks.length} selected
                            </span>
                            <span className="selection-help">
                              Click rows to select • Ctrl+A for all • Esc to
                              clear
                            </span>
                            {/* <IonButton
                              fill="clear"
                              size="small"
                              color="secondary"
                              onClick={handleBulkEditTags}
                              disabled={
                                isAddingFolder ||
                                scanningProgress.isScanning ||
                                isDeletingFolder ||
                                isImportingSerato
                              }
                            >
                              <IonIcon slot="start" icon={pencilOutline} />
                              Edit Tags
                            </IonButton> */}
                            {/* <IonButton
                              fill="clear"
                              size="small"
                              color="tertiary"
                              onClick={handleBulkRate}
                              disabled={
                                isAddingFolder ||
                                scanningProgress.isScanning ||
                                isDeletingFolder ||
                                isImportingSerato
                              }
                            >
                              <IonIcon slot="start" icon={star} />
                              Rate
                            </IonButton> */}
                            <IonButton
                              fill="clear"
                              size="small"
                              color="danger"
                              onClick={handleBulkRemove}
                              disabled={
                                isAddingFolder ||
                                scanningProgress.isScanning ||
                                isDeletingFolder ||
                                isImportingSerato
                              }
                            >
                              <IonIcon slot="start" icon={closeOutline} />
                              {isDeletingFolder ? 'Removing...' : 'Remove'}
                              {isDeletingFolder && (
                                <IonSpinner slot="end" name="dots" />
                              )}
                            </IonButton>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Secondary Filter Row - Show when artists, genres, or import sources are selected */}
                    {(activeFilter === 'artists' ||
                      activeFilter === 'genres' ||
                      activeFilter === 'importSources') && (
                      <div className="secondary-filter-row">
                        {activeFilter === 'importSources' && null}
                        <div className="filter-tiles-scrollable">
                          {activeFilter === 'artists' &&
                            topArtistsMap.map(([artist, count]) => (
                              <div
                                key={artist}
                                className={`filter-tile ${selectedArtist === artist ? 'active' : ''}`}
                                onClick={() => handleArtistClick(artist)}
                              >
                                <div className="filter-tile-name">{artist}</div>
                                <div className="filter-tile-count">
                                  {count} tracks
                                </div>
                              </div>
                            ))}
                          {activeFilter === 'genres' &&
                            topGenresMap.map(([genre, count]) => (
                              <div
                                key={genre}
                                className={`filter-tile ${selectedGenre === genre ? 'active' : ''}`}
                                onClick={() => handleGenreClick(genre)}
                              >
                                <div className="filter-tile-name">{genre}</div>
                                <div className="filter-tile-count">
                                  {count} tracks
                                </div>
                              </div>
                            ))}
                          {activeFilter === 'importSources' &&
                            importSourcesMap.map(([source, count]) => (
                              <div
                                key={source}
                                className={`filter-tile ${selectedImportSource === source ? 'active' : ''}`}
                                onClick={() => handleImportSourceClick(source)}
                              >
                                <div className="filter-tile-name">
                                  {source === 'serato' && 'In Serato'}
                                  {source === 'virtualdj' && 'In VirtualDJ'}
                                  {source === 'folder' && 'In Folders'}
                                  {!['serato', 'virtualdj', 'folder'].includes(
                                    source,
                                  ) && `In ${source}`}
                                </div>
                                <div className="filter-tile-count">
                                  {count.toLocaleString()} tracks
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Progress Indicators */}
                    {(scanningProgress.isScanning ||
                      deletionProgress.isDeleting) && (
                      <div className="operation-progress-overlay">
                        <div className="progress-content">
                          {scanningProgress.isScanning && (
                            <div className="scanning-progress">
                              <div className="progress-header">
                                <IonIcon
                                  icon={folderOpenOutline}
                                  className="progress-icon"
                                />
                                <div className="progress-text">
                                  <h3>Scanning Folder</h3>
                                  <p>{scanningProgress.currentFolder}</p>
                                </div>
                                <IonSpinner name="dots" />
                              </div>
                              {(scanningProgress.totalFiles ?? 0) > 0 && (
                                <div className="progress-details">
                                  <div className="progress-bar">
                                    <div
                                      className="progress-fill"
                                      style={{
                                        width: `${scanningProgress.progress}%`,
                                      }}
                                    ></div>
                                  </div>
                                  <div className="metadata-content">
                                    <div className="album-art-placeholder">
                                      <IonIcon icon={musicalNotesOutline} />
                                    </div>
                                    <div className="metadata-fields-container">
                                      {Object.entries(selectedTrack || {}).map(
                                        ([key, value]) => {
                                          if (
                                            key === 'tags' &&
                                            typeof value === 'object' &&
                                            value !== null
                                          ) {
                                            return (
                                              <div
                                                key="tags-group"
                                                className="metadata-group tags-group"
                                              >
                                                <div className="metadata-group-header">
                                                  <h5 className="group-title">
                                                    Tags
                                                  </h5>
                                                  {!isEditingTags ? (
                                                    <div className="tag-buttons">
                                                      {/* <button
                                                        className="auto-fill-button"
                                                        onClick={
                                                          handleOpenAutoFillModal
                                                        }
                                                      >
                                                        Auto Fill
                                                      </button> */}
                                                      <button
                                                        className="edit-button"
                                                        onClick={() =>
                                                          setIsEditingTags(true)
                                                        }
                                                      >
                                                        <IonIcon
                                                          icon={createOutline}
                                                        />
                                                      </button>
                                                    </div>
                                                  ) : (
                                                    <div className="edit-actions">
                                                      <button
                                                        className="save-button"
                                                        onClick={handleSaveTags}
                                                      >
                                                        <IonIcon
                                                          icon={
                                                            checkmarkOutline
                                                          }
                                                        />
                                                      </button>
                                                      <button
                                                        className="close-button"
                                                        onClick={() => {
                                                          setIsEditingTags(
                                                            false,
                                                          );
                                                          if (
                                                            selectedTrack?.tags
                                                          ) {
                                                            setEditedTags({
                                                              ...selectedTrack.tags,
                                                            });
                                                          }
                                                        }}
                                                      >
                                                        <IonIcon
                                                          icon={closeOutline}
                                                        />
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                                {metadataUpdating ? (
                                                  <div className="metadata-loading">
                                                    <IonSpinner name="crescent" />
                                                    <span>
                                                      Updating metadata...
                                                    </span>
                                                  </div>
                                                ) : (
                                                  Object.entries(
                                                    editedTags,
                                                  ).map(
                                                    ([tagKey, tagValue]) => (
                                                      <div
                                                        key={`tag-${tagKey}`}
                                                        className="metadata-field tag-field"
                                                      >
                                                        <span className="metadata-label">
                                                          {tagKey}
                                                        </span>
                                                        {isEditingTags &&
                                                        !nonEditableTags.includes(
                                                          tagKey,
                                                        ) ? (
                                                          renderInputField(
                                                            tagKey,
                                                            tagValue,
                                                          )
                                                        ) : (
                                                          <span className="metadata-value">
                                                            {renderDisplayValue(
                                                              tagKey,
                                                              tagValue,
                                                            )}
                                                          </span>
                                                        )}
                                                      </div>
                                                    ),
                                                  )
                                                )}
                                              </div>
                                            );
                                          }
                                          if (key !== 'tags') {
                                            return (
                                              <div
                                                key={key}
                                                className="metadata-field top-level-field"
                                              >
                                                <span className="metadata-label">
                                                  {key}
                                                </span>
                                                <span className="metadata-value">
                                                  {Array.isArray(value)
                                                    ? value.join(', ')
                                                    : typeof value === 'object'
                                                      ? JSON.stringify(
                                                          value,
                                                          null,
                                                          2,
                                                        )
                                                      : String(value ?? 'N/A')}
                                                </span>
                                              </div>
                                            );
                                          }
                                          return null;
                                        },
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {deletionProgress.isDeleting && (
                            <div className="deletion-progress">
                              <div className="progress-header">
                                <IonIcon
                                  icon={trashOutline}
                                  className="progress-icon deletion"
                                />
                                <div className="progress-text">
                                  <h3>Deleting Folder</h3>
                                  <p>{deletionProgress.message}</p>
                                </div>
                                <IonSpinner name="dots" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Track Table */}
                    <div className="track-table-container">
                      {/* Loading indicator for infinite scroll */}
                      {isLoadingMore && (
                        <div className="loading-more-indicator">
                          <IonSpinner name="crescent" />
                          <span>
                            Loading more tracks... ({rowData.length} of{' '}
                            {filteredTrackCount})
                          </span>
                        </div>
                      )}

                      {visibleColumns.length === 0 ? (
                        <div className="empty-state-container">
                          <div className="empty-state-icon-wrapper">
                            <IonIcon
                              icon={eyeOffOutline}
                              className="empty-state-icon"
                            />
                          </div>
                          <h2 className="empty-state-title">
                            No Columns Selected
                          </h2>
                          <p className="empty-state-subtitle">
                            Please select at least one column from the Column
                            Settings to view your tracks.
                          </p>
                          <button
                            className="reset-button"
                            onClick={resetColumnOrder}
                            style={{
                              marginTop: '20px',
                              padding: '10px 20px',
                              background: 'var(--ion-color-primary, #ea580c)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(234, 88, 12, 0.2)',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            Reset Columns
                          </button>
                        </div>
                      ) : (
                        /* Virtual Scroll Implementation */
                        <div className="virtual-scroll-container">
                          <div
                            className="table-scroll-wrapper"
                            style={{ minWidth: `${tableMinWidth}px` }}
                          >
                            {/* Virtual Table Header */}
                            <div className="virtual-table-header">
                              {columnConfigs
                                .filter((col) => col.visible)
                                .sort((a, b) => a.order - b.order)
                                .map((column) => {
                                  const sortField =
                                    column.key === 'seratoPlayCount'
                                      ? 'seratoPlayCount'
                                      : `tags.${column.key}`;
                                  const isTitle = column.key === 'title';

                                  return (
                                    <div
                                      key={column.key}
                                      className={`virtual-header-cell ${column.key}-header ${
                                        column.sortable ? 'sortable-header' : ''
                                      }`}
                                      style={{
                                        width: column.width,
                                        minWidth: column.minWidth,
                                        flex: column.flex,
                                      }}
                                      onClick={
                                        column.sortable
                                          ? () =>
                                              applySorting(
                                                sortField,
                                                currentSortField ===
                                                  sortField &&
                                                  currentSortOrder === 'ASC'
                                                  ? 'DESC'
                                                  : 'ASC',
                                              )
                                          : undefined
                                      }
                                    >
                                      <span>
                                        {column.label}{' '}
                                        {column.sortable &&
                                          currentSortField === sortField &&
                                          (currentSortOrder === 'ASC'
                                            ? '↑'
                                            : '↓')}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* Virtual Scroll Content */}
                            <Virtuoso
                              style={{ height: '100%' }}
                              data={filteredTracks}
                              itemContent={(index, track) => (
                                <TrackRow
                                  key={track.id}
                                  track={track}
                                  index={index}
                                  // isSelected={isTrackSelected(track)} // COMMENTED OUT: Selection
                                  columnConfigs={columnConfigs}
                                  onRowClick={handleVirtualRowClick}
                                  onRowDoubleClick={handleRowDoubleClick}
                                  setRowData={setRowData}
                                  setFilteredTracks={setFilteredTracks}
                                  refreshStats={refreshStats}
                                />
                              )}
                              className="ion-content-scroll-host"
                              endReached={() => {
                                if (hasMoreData && !isLoadingMore) {
                                  console.log(
                                    '🔄 Virtuoso endReached - triggering loadMoreData',
                                  );
                                  loadMoreData();
                                }
                              }}
                              overscan={200}
                              components={{
                                Footer: () =>
                                  isLoadingMore ? (
                                    <div
                                      style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <IonSpinner name="crescent" />
                                      <span style={{ marginLeft: '8px' }}>
                                        Loading more tracks... (
                                        {filteredTracks.length} of{' '}
                                        {filteredTrackCount})
                                      </span>
                                    </div>
                                  ) : null,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
      {metadataPanelVisible && selectedTrack && (
        <div className={`track-metadata-editor visible`}>
          {/* Panel header */}
          <div className="metadata-header">
            <h3>Edit Metadata</h3>
            <div className="header-buttons">
              <button
                className="close-button"
                onClick={() => setMetadataPanelVisible(false)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </div>
          </div>
          {/* Panel content */}
          <div className="metadata-content">
            {/* Render metadata fields for selectedTrack */}

            {/* Tags Section */}
            {selectedTrack?.tags && (
              <div key="tags-group" className="metadata-group tags-group">
                <div className="metadata-group-header">
                  <h5 className="group-title">Tags</h5>
                  {!isEditingTags ? (
                    <div className="tag-buttons">
                      {/* <button
                        className="auto-fill-button"
                        onClick={handleOpenAutoFillModal}
                      >
                        Auto Fill
                      </button> */}
                      <button
                        className="edit-button"
                        onClick={() => setIsEditingTags(true)}
                      >
                        <IonIcon icon={createOutline} />
                      </button>
                    </div>
                  ) : (
                    <div className="edit-actions">
                      <button className="save-button" onClick={handleSaveTags}>
                        <IonIcon icon={checkmarkOutline} />
                      </button>
                      <button
                        className="close-button"
                        onClick={() => {
                          setIsEditingTags(false);
                          if (selectedTrack?.tags)
                            setEditedTags({ ...selectedTrack.tags });
                        }}
                      >
                        <IonIcon icon={closeOutline} />
                      </button>
                    </div>
                  )}
                </div>
                {metadataUpdating ? (
                  <div className="metadata-loading">
                    <IonSpinner name="crescent" />
                    <span>Updating metadata...</span>
                  </div>
                ) : (
                  Object.entries(editedTags).map(([tagKey, tagValue]) => (
                    <div
                      key={`tag-${tagKey}`}
                      className="metadata-field tag-field"
                    >
                      <span className="metadata-label">
                        {getFieldLabel(tagKey)}
                      </span>
                      {isEditingTags && !nonEditableTags.includes(tagKey) ? (
                        renderInputField(tagKey, tagValue)
                      ) : (
                        <span className="metadata-value">
                          {renderDisplayValue(tagKey, tagValue)}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* File Information Section */}
            <div className="metadata-group file-info-group">
              <h5 className="group-title">File Information</h5>
              {selectedTrack && (
                <>
                  {getDisplayableFields(selectedTrack)
                    .filter(
                      ([key]) =>
                        key === 'filePath' || key === 'absoluteNormalizedPath',
                    )
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className={`metadata-field top-level-field field-${key}`}
                      >
                        <span className="metadata-label">
                          {getFieldLabel(key)}
                        </span>
                        <span className="metadata-value">
                          {formatFieldValue(key, value)}
                        </span>
                      </div>
                    ))}

                  {getDisplayableFields(selectedTrack)
                    .filter(
                      ([key]) =>
                        key !== 'filePath' && key !== 'absoluteNormalizedPath',
                    )
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className={`metadata-field top-level-field field-${key}`}
                      >
                        <span className="metadata-label">
                          {getFieldLabel(key)}
                        </span>
                        <span className="metadata-value">
                          {formatFieldValue(key, value)}
                        </span>
                      </div>
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showAutoFillModal && (
        <div className="auto-fill-popup">
          <h4>Auto Fill Metadata</h4>
          <div className="checkboxes">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={autoFillFields.album}
                onChange={() =>
                  setAutoFillFields((f) => ({ ...f, album: !f.album }))
                }
              />
              <span className="checkbox-label">Album</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={autoFillFields.bpm}
                onChange={() =>
                  setAutoFillFields((f) => ({ ...f, bpm: !f.bpm }))
                }
              />
              <span className="checkbox-label">BPM</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={autoFillFields.genre}
                onChange={() =>
                  setAutoFillFields((f) => ({ ...f, genre: !f.genre }))
                }
              />
              <span className="checkbox-label">Genre</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={autoFillFields.year}
                onChange={() =>
                  setAutoFillFields((f) => ({ ...f, year: !f.year }))
                }
              />
              <span className="checkbox-label">Year</span>
            </label>
          </div>
          <div className="modal-actions">
            <button className="update-button" onClick={handleRunAutoFill}>
              Update
            </button>
            <button
              className="cancel-button"
              onClick={() => setShowAutoFillModal(false)}
            >
              Cancel
            </button>
          </div>
          {metadataUpdating && (
            <div className="auto-fill-spinner-overlay">
              <div className="spinner" />
            </div>
          )}
        </div>
      )}
      {isBulkEditingTags && (
        <div className="bulk-edit-panel">
          <h4>Bulk Edit Tags</h4>
          {Object.entries(bulkEditedTags).map(([key, value]) => (
            <div key={key} className="metadata-field tag-field">
              <span className="metadata-label">{key}</span>
              {renderInputField(key, value, (v: any) =>
                handleBulkTagChange(key, v),
              )}
            </div>
          ))}
          <div className="bulk-edit-actions">
            <button className="save-button" onClick={handleBulkSaveTags}>
              Save
            </button>
            <button
              className="close-button"
              onClick={() => setIsBulkEditingTags(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Column Management Modal */}
      <ColumnManagementModal
        isOpen={columnManagerOpen}
        onClose={() => setColumnManagerOpen(false)}
        columns={columnConfigs}
        onColumnToggle={handleColumnToggle}
        onColumnReorder={handleColumnReorder}
        onReset={resetColumnOrder}
        onSelectAll={handleSelectAllColumns}
      />
    </IonContent>
  );
};

export default MyLibrary;
